import { setBelongingInformation } from "@vrf/fr-vrf-auth-core";
import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import {
  Cell,
  SpreadsheetTemplate,
} from "@vrf/fr-vrf-spreadsheet-core-templating";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfFileUploadField,
  FrVrfFooter,
  FrVrfTextDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CellItem,
  CellStyle,
  InterfaceRequirementListPageInitialState,
  InterfacefRequirement,
  Option,
  SpreadsheetRow,
  SpreadsheetState,
  keyIfItems,
  metaOnlyEditableItems,
  recordSet,
  usePageState,
} from ".";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import { useGetDataKind } from "../../services/data-kinds";
import {
  useGetDipByRegionList,
  useGetEdhProtocolList,
  useGetEdiChainTypeList,
  useGetGeneralCodes,
} from "../../services/data-list";
import {
  UpdateIfList,
  useGetIFDefinitions,
  useGetIfDefinitionForReference,
  useGetIfDefinitionCommonValidationResult,
  useIfDefinitionReport,
  useUpdateIfDefinitions,
  BoundaryId,
  GetIfDefinitionForReferenceResult,
  GetIfDefinitionForReferenceRequest,
  GetIfDefinitionForReferenceResponse,
  IfDefinition,
} from "../../services/if-definitions";
import { useGetProject } from "../../services/project";
import { useGetLoginUserRoleList } from "../../services/data-list";
import { useGetActionItems } from "../../services/screen-authorization/ScreenAuthorizationHook";
import { ENV } from "@vrf/fr-vrf-core";
import {
  BdryType,
  IfReqCnclType,
  IfCycleType,
  IfCallerScreenType,
  InDefCommonFlagType,
  InterfaceRequirementType,
  IoType,
  PtnrType,
  RoleCvrgType,
  RoleType,
  TcktStsTypePrjPrgrs,
  ValidationTypeCode,
  EdhPrtclType,
} from "../../util/general-code/enums/general-codes";
import {
  ErrorResponse,
  mappingErrorResponseToSpreadsheet,
} from "../../util/spreadsheet-data-utils";

export enum SrcDstType {
  Source = "src",
  Destination = "dst",
}

export enum RequireType {
  Mandatory = "mnd",
  Optional = "opt",
  NoRequire = "noreq",
}

export enum EditableType {
  Yes = "Y",
  No = "N",
}

const MANUAL_LINK = {
  tooltip: "7. Design IF Requirements",
  value:
    "https://confluence.fastretailing.com/pages/viewpage.action?pageId=817541410",
};

const TEMPLATE_FILE_URL = `spreadsheet-template/interface-requirement-list.xlsx`;
const REPORT_TYPE_IF_REQUIREMENT = "if-req";
const DSGN_W_FLW_STS_CANCELLED = "Cancelled";
const DPlYMNT_W_FLW_STS_DEPLOYED = "Deployed";
const DPlYMNT_W_FLW_STS_CANCELLED = "Cancelled";

export const InterfaceRequirementListPageContent: React.FC = () => {
  const navigate = useNavigate();
  const { showToast, showAlertBar, showConfirmationDialog, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const [getGeneralCodesResponse, getGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getDipByRegionListResponse, getDipByRegionListRequest] =
    useGetDipByRegionList();
  const [getEdhProtocolListResponse, getEdhProtocolListRequest] =
    useGetEdhProtocolList();
  const [getEdiChainTypeListResponse, getEdiChainTypeListRequest] =
    useGetEdiChainTypeList();
  const [getDataKindResponse, getDataKindRequest] = useGetDataKind();
  const [getIfDefinitionsResponse, getIfDefinitionsRequest] =
    useGetIFDefinitions();
  const [
    getIfDefinitionForReferenceResponse,
    getIfDefinitionForReferenceRequest,
  ] = useGetIfDefinitionForReference();
  const [
    getIfRefBybulkEditOfInbdIDResponse,
    getIfRefBybulkEditOfInbdIDRequest,
  ] = useGetIfDefinitionForReference();
  const [
    getIfRefBybulkEditOfOutbdIDResponse,
    getIfRefBybulkEditOfOutbdIDRequest,
  ] = useGetIfDefinitionForReference();
  const [getIfRefBybulkEditOfDipResponse, getIfRefBybulkEditOfDipRequest] =
    useGetIfDefinitionForReference();
  const [getProjectResponse, getProjectRequest] = useGetProject();
  const [getLoginUserRoleListResponse, getLoginUserRoleListRequest] =
    useGetLoginUserRoleList();
  const [updateIfDefinitionsResponse, updateIfDefinitionsRequest] =
    useUpdateIfDefinitions();
  const [, ifDefinitionReportRequest] = useIfDefinitionReport();
  const [
    getIfDefinitionCommonValidationResultResponse,
    getIfDefinitionCommonValidationResultRequest,
  ] = useGetIfDefinitionCommonValidationResult();
  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const [authdActionItems, sendGetActionItems] = useGetActionItems();
  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const cellItemsAll = Object.keys(recordSet) as Array<keyof CellStyle>;
  const dfnSetVerNum = ENV("REACT_APP_DFN_SET_VER_NUM");
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const params = {
    pjtId: queryParameter.get("pjtId") ?? "",
  };

  const onInitialDisplayed = (msg?: string): void => {
    setAppState({
      ...appState,
      title: `Interface Requirement List`,
      pjtId: params.pjtId,
    });

    getLoginUserRoleListRequest({ pjtId: params.pjtId });

    setBelongingInformation({
      belongTypeCode: RoleCvrgType.PROJECT,
      belongCode: !!params.pjtId ? params.pjtId : appState.pjtId,
    });

    sendGetActionItems({
      blngTypeCd: RoleCvrgType.PROJECT,
      blngCd: !!params.pjtId ? params.pjtId : appState.pjtId,
    });

    const cdTypes = [
      "in_def_common_flag_type",
      "ptnr_type",
      "ptnr_bu_cntry_type",
      "interface_requirement_type",
      "security_information_level_type",
      "encription_type",
      "snapshot_incremental_delta_type",
      "if_cycle_type",
      "data_scope_type",
      "file_type_type",
      "edi_polling_adaptor_type",
      "send_datalake_type",
      "if_req_cncl_type",
    ];
    getGeneralCodesRequest({ cdTypes: cdTypes });
    getDipByRegionListRequest({});
    getEdhProtocolListRequest({});
    getEdiChainTypeListRequest({ godOnlyFlag: InDefCommonFlagType.NO });
    getDataKindRequest({});
    getProjectRequest({
      pjtId: params.pjtId,
      dfnSetVerNum: dfnSetVerNum,
    });
    getIfDefinitionsRequest({
      dfnSetVerNum: dfnSetVerNum,
      project: {
        pjtIds: [params.pjtId],
      },
    });

    setPageState((prevState) => ({
      ...prevState,
      infoBar: {
        isShow: msg ? true : false,
        message: msg ?? "",
        ready: "",
      },
    }));
  };
  useEffect(onInitialDisplayed, []);

  const onListenGetGeneralCodesResponse = () => {
    let inDefCommonFlagType = [] as Option[];
    let ptnrType = [] as Option[];
    let ptnrBuCntryType = [] as Option[];
    let interfaceRequirementType = [] as Option[];
    let securityInformationLevelType = [] as Option[];
    let encriptionType = [] as Option[];
    let snapshotIncrementalDeltaType = [] as Option[];
    let ifCycleType = [] as Option[];
    let dataScopeType = [] as Option[];
    let fileTypeType = [] as Option[];
    let ediPollingAdaptorType = [] as Option[];
    let sendDatalakeType = [] as Option[];
    let ifReqCnclType = [] as Option[];

    const generalCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];

    generalCode.forEach((e) => {
      switch (e.cdType) {
        case "in_def_common_flag_type":
          inDefCommonFlagType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "ptnr_type":
          ptnrType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "ptnr_bu_cntry_type":
          ptnrBuCntryType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "interface_requirement_type":
          interfaceRequirementType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "security_information_level_type":
          securityInformationLevelType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "encription_type":
          encriptionType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "snapshot_incremental_delta_type":
          snapshotIncrementalDeltaType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "if_cycle_type":
          ifCycleType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "data_scope_type":
          dataScopeType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "file_type_type":
          fileTypeType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "edi_polling_adaptor_type":
          ediPollingAdaptorType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "send_datalake_type":
          sendDatalakeType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "if_req_cncl_type":
          ifReqCnclType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
      }
    });

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        inDefCommonFlagType: inDefCommonFlagType,
        ptnrType: ptnrType,
        ptnrBuCntryType: ptnrBuCntryType,
        interfaceRequirementType: interfaceRequirementType,
        securityInformationLevelType: securityInformationLevelType,
        encriptionType: encriptionType,
        snapshotIncrementalDeltaType: snapshotIncrementalDeltaType,
        ifCycleType: ifCycleType,
        dataScopeType: dataScopeType,
        fileTypeType: fileTypeType,
        ediPollingAdaptorType: ediPollingAdaptorType,
        sendDatalakeType: sendDatalakeType,
        ifReqCnclType: ifReqCnclType,
      },
    }));
  };
  useEffect(onListenGetGeneralCodesResponse, [getGeneralCodesResponse]);

  const onListenGetDipByRegionListResponse = () => {
    if (getDipByRegionListResponse === undefined) return;

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        dipByRegionList: getDipByRegionListResponse.dipByRgnList.map((e) => ({
          label: e.dipByRgn,
          value: e.dipByRgn,
        })),
      },
    }));
  };
  useEffect(onListenGetDipByRegionListResponse, [getDipByRegionListResponse]);

  const onListenGetDataKindResponse = () => {
    if (getDataKindResponse === undefined) return;

    const enableDataKinds = getDataKindResponse.dataKindList.filter(
      (e) => e.delFlg === InDefCommonFlagType.NO,
    );

    setPageState((prevState) => ({
      ...prevState,
      dataKind: {
        ...getDataKindResponse,
        dataKindList: enableDataKinds,
      },
    }));
  };
  useEffect(onListenGetDataKindResponse, [getDataKindResponse]);

  const onListenGetEdhProtocolListResponse = () => {
    if (getEdhProtocolListResponse === undefined) return;

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        inboundEdhProtocolList: getEdhProtocolListResponse.edhProtocolList
          // JDI-2396 Enable selection of Inbound GCS protocol
          .filter(
            (e) =>
              e.edhPrtclTypeCd === EdhPrtclType.GCS ||
              e.godOnlyFlag === InDefCommonFlagType.NO,
          )
          .filter((e) =>
            [IoType.INBOUND, IoType.INBOUNDOUTBOUND].includes(
              e.inOutTypeCd as IoType,
            ),
          )
          .map((e) => ({
            label: e.label,
            value: e.value,
          })),
        outboundEdhProtocolList: getEdhProtocolListResponse.edhProtocolList
          .filter((e) => e.godOnlyFlag === InDefCommonFlagType.NO)
          .filter((e) =>
            [IoType.OUTBOUND, IoType.INBOUNDOUTBOUND].includes(
              e.inOutTypeCd as IoType,
            ),
          )
          .map((e) => ({
            label: e.label,
            value: e.value,
          })),
      },
    }));
  };
  useEffect(onListenGetEdhProtocolListResponse, [getEdhProtocolListResponse]);

  const onListenGetEdiChainTypeListResponse = () => {
    if (getEdiChainTypeListResponse === undefined) return;

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        srcEdiChainTypeList: getEdiChainTypeListResponse.ediChainTypeList
          .filter((e) =>
            [IoType.INBOUND, IoType.INBOUNDOUTBOUND].includes(
              e.inOutTypeCd as IoType,
            ),
          )
          .map((e) => ({
            label: e.label,
            value: e.value,
          })),
        dstEdiChainTypeList: getEdiChainTypeListResponse.ediChainTypeList
          .filter((e) =>
            [IoType.OUTBOUND, IoType.INBOUNDOUTBOUND].includes(
              e.inOutTypeCd as IoType,
            ),
          )
          .map((e) => ({
            label: e.label,
            value: e.value,
          })),
      },
    }));
  };
  useEffect(onListenGetEdiChainTypeListResponse, [getEdiChainTypeListResponse]);

  const onListenGetIfDefinitionsResponse = (): void => {
    if (getIfDefinitionsResponse === undefined) return;

    const interfacefRequirement: InterfacefRequirement[] =
      getIfDefinitionsResponse.ifList.map((e) => {
        return { ...e };
      });
    setPageState((prevState) => ({
      ...prevState,
      result: {
        total: getIfDefinitionsResponse.total,
        ifList: interfacefRequirement,
      },
    }));
  };
  useEffect(onListenGetIfDefinitionsResponse, [getIfDefinitionsResponse]);

  const onListenGetProjectResponse = (): void => {
    if (getProjectResponse === undefined) return;

    setPageState((prevState) => ({
      ...prevState,
      project: getProjectResponse,
    }));

    const pageName = `Project Name: ${getProjectResponse.projectInformation.pjtName}`;
    setAppState({
      ...appState,
      title: `Interface Requirement List ( ${pageName} )`,
    });
  };
  useEffect(onListenGetProjectResponse, [getProjectResponse]);

  const onListenGetLoginUserRoleListResponse = (): void => {
    if (getLoginUserRoleListResponse === undefined) return;

    setPageState((prevState) => ({
      ...prevState,
      loginUserRoleList: getLoginUserRoleListResponse.userRoleList,
    }));
  };
  useEffect(onListenGetLoginUserRoleListResponse, [
    getLoginUserRoleListResponse,
  ]);

  const onListenGetAuthdActionItemsResponse = () => {
    if (!authdActionItems) return;
    setPageState((prevState) => ({
      ...prevState,
      authorized: true,
    }));
  };
  useEffect(onListenGetAuthdActionItemsResponse, [authdActionItems]);

  // Style change as value changes
  const requireControll = (target: string, spreadsheetRow: SpreadsheetRow) => {
    const targetItems: Array<keyof CellStyle> = [];
    switch (target) {
      case "src.interfaceDefinition.metaOnlyFlag":
        if (
          spreadsheetRow.src?.interfaceDefinition.metaOnlyFlag ===
          InDefCommonFlagType.YES
        ) {
          spreadsheetRow = updateRecordSetDef(
            spreadsheetRow,
            spreadsheetRow.src?.interfaceDefinition
              .interfaceRequirementType as InterfaceRequirementType,
            SrcDstType.Source,
          );
          spreadsheetRow = updateMetaOnlyEditable(
            spreadsheetRow,
            SrcDstType.Source,
          );
        } else {
          spreadsheetRow = updateRecordSetDef(
            spreadsheetRow,
            spreadsheetRow.src?.interfaceDefinition
              .interfaceRequirementType as InterfaceRequirementType,
            SrcDstType.Source,
          );
        }
        break;
      case "dst.interfaceDefinition.metaOnlyFlag":
        if (
          spreadsheetRow.dst?.interfaceDefinition.metaOnlyFlag ===
          InDefCommonFlagType.YES
        ) {
          spreadsheetRow = updateRecordSetDef(
            spreadsheetRow,
            spreadsheetRow.dst?.interfaceDefinition
              .interfaceRequirementType as InterfaceRequirementType,
            SrcDstType.Destination,
          );
          spreadsheetRow = updateMetaOnlyEditable(
            spreadsheetRow,
            SrcDstType.Destination,
          );
        } else {
          spreadsheetRow = updateRecordSetDef(
            spreadsheetRow,
            spreadsheetRow.dst?.interfaceDefinition
              .interfaceRequirementType as InterfaceRequirementType,
            SrcDstType.Destination,
          );
        }
        break;

      case "src.interfaceDefinition.interfaceRequirementType":
        spreadsheetRow = updateRecordSetDef(
          spreadsheetRow,
          spreadsheetRow.src?.interfaceDefinition
            .interfaceRequirementType as InterfaceRequirementType,
          SrcDstType.Source,
        );
        requireControllForDataKind(spreadsheetRow);
        break;
      case "dst.interfaceDefinition.interfaceRequirementType":
        spreadsheetRow = updateRecordSetDef(
          spreadsheetRow,
          spreadsheetRow.dst?.interfaceDefinition
            .interfaceRequirementType as InterfaceRequirementType,
          SrcDstType.Destination,
        );
        requireControllForDataKind(spreadsheetRow);
        break;
      case "src.interfaceDefinition.dataKindId":
        targetItems.push(
          "src.interfaceDefinition.interfaceNameEnglish",
          "src.interfaceDefinition.interfaceNameJapanese",
          "project.mltcstDsbldFlg",
        );
        if (
          !["", null, undefined].includes(
            spreadsheetRow.src?.interfaceDefinition.dataKindId,
          )
        ) {
          spreadsheetRow.require["project.dtKindIdIssueNumGrpCd"] =
            RequireType.NoRequire;
          targetItems.forEach((item) => {
            spreadsheetRow.require[item] = RequireType.NoRequire;
          });
        } else {
          spreadsheetRow.require["project.dtKindIdIssueNumGrpCd"] =
            RequireType.Optional;
          targetItems.forEach((item) => {
            spreadsheetRow.require[item] = RequireType.Mandatory;
          });
        }
        break;
      // Style change of related items due to inbound partner code change.
      case "src.interfaceDefinition.inbound.inboundPartner.partnerCode":
        targetItems.push(
          "src.interfaceDefinition.inbound.inboundPartner.partnerType",
          "src.interfaceDefinition.inbound.inboundPartner.chainTypeCode",
          "src.interfaceDefinition.inbound.inboundPartner.fileTypeCode",
          "src.interfaceDefinition.inbound.inboundPartner.pollingAdaptorTypeCode",
        );
        if (
          !["", null, undefined].includes(
            spreadsheetRow.src?.interfaceDefinition.inbound?.inboundPartner
              ?.partnerCode,
          )
        ) {
          targetItems.forEach((item) => {
            spreadsheetRow.require[item] = RequireType.Mandatory;
          });
          if (
            spreadsheetRow.src?.interfaceDefinition.inbound?.inboundPartner
              ?.partnerType === PtnrType.LOGISTICS
          ) {
            spreadsheetRow.require[
              "src.interfaceDefinition.inbound.inboundPartner.partnerBusiness"
            ] = RequireType.Mandatory;
          } else {
            spreadsheetRow.require[
              "src.interfaceDefinition.inbound.inboundPartner.partnerBusiness"
            ] = RequireType.NoRequire;
          }
          // Optional for Partnerifid JDI-2334
          spreadsheetRow.require[
            "src.interfaceDefinition.inbound.inboundPartner.partnerIfId"
          ] = RequireType.Optional;
        } else {
          //Changes due to partner code clearing
          targetItems.forEach((item) => {
            spreadsheetRow.require[item] = RequireType.NoRequire;
          });
          spreadsheetRow.require[
            "src.interfaceDefinition.inbound.inboundPartner.partnerBusiness"
          ] = RequireType.NoRequire;
          spreadsheetRow.require[
            "src.interfaceDefinition.inbound.inboundPartner.partnerIfId"
          ] = RequireType.NoRequire;
        }
        break;
      // Style change of related items due to Outbound partner code change.
      case "dst.interfaceDefinition.outbound.outboundPartner.partnerCode":
        targetItems.push(
          "dst.interfaceDefinition.outbound.outboundPartner.partnerType",
          "dst.interfaceDefinition.outbound.outboundPartner.chainTypeCode",
          "dst.interfaceDefinition.outbound.outboundPartner.fileTypeCode",
        );
        if (
          !["", null, undefined].includes(
            spreadsheetRow.dst?.interfaceDefinition.outbound?.outboundPartner
              ?.partnerCode,
          )
        ) {
          targetItems.forEach((item) => {
            spreadsheetRow.require[item] = RequireType.Mandatory;
          });
          if (
            spreadsheetRow.dst?.interfaceDefinition.outbound?.outboundPartner
              ?.partnerType === PtnrType.LOGISTICS
          ) {
            spreadsheetRow.require[
              "dst.interfaceDefinition.outbound.outboundPartner.partnerBusiness"
            ] = RequireType.Mandatory;
          } else {
            spreadsheetRow.require[
              "dst.interfaceDefinition.outbound.outboundPartner.partnerBusiness"
            ] = RequireType.NoRequire;
          }
          // Optional for Partnerifid JDI-2334
          spreadsheetRow.require[
            "dst.interfaceDefinition.outbound.outboundPartner.partnerIfId"
          ] = RequireType.Optional;
        } else {
          // Changes due to partner code clearing
          targetItems.forEach((item) => {
            spreadsheetRow.require[item] = RequireType.NoRequire;
          });
          spreadsheetRow.require[
            "dst.interfaceDefinition.outbound.outboundPartner.partnerBusiness"
          ] = RequireType.NoRequire;
          spreadsheetRow.require[
            "dst.interfaceDefinition.outbound.outboundPartner.partnerIfId"
          ] = RequireType.NoRequire;
        }
        break;
      case "src.interfaceDefinition.inbound.inboundPartner.partnerType":
        if (
          spreadsheetRow.src?.interfaceDefinition.inbound?.inboundPartner
            ?.partnerType === PtnrType.LOGISTICS &&
          !["", null, undefined].includes(
            spreadsheetRow.src?.interfaceDefinition.inbound?.inboundPartner
              ?.partnerCode,
          )
        ) {
          spreadsheetRow.require[
            "src.interfaceDefinition.inbound.inboundPartner.partnerBusiness"
          ] = RequireType.Mandatory;
        } else {
          spreadsheetRow.require[
            "src.interfaceDefinition.inbound.inboundPartner.partnerBusiness"
          ] = RequireType.NoRequire;
        }
        break;
      case "dst.interfaceDefinition.outbound.outboundPartner.partnerType":
        if (
          spreadsheetRow.dst?.interfaceDefinition.outbound?.outboundPartner
            ?.partnerType === PtnrType.LOGISTICS &&
          !["", null, undefined].includes(
            spreadsheetRow.dst?.interfaceDefinition.outbound?.outboundPartner
              ?.partnerCode,
          )
        ) {
          spreadsheetRow.require[
            "dst.interfaceDefinition.outbound.outboundPartner.partnerBusiness"
          ] = RequireType.Mandatory;
        } else {
          spreadsheetRow.require[
            "dst.interfaceDefinition.outbound.outboundPartner.partnerBusiness"
          ] = RequireType.NoRequire;
        }
        break;
      case "src.interfaceDefinition.inbound.useConverter":
        if (
          ![InDefCommonFlagType.NO, null, ""].includes(
            spreadsheetRow.src?.interfaceDefinition.inbound
              ?.useConverter as InDefCommonFlagType,
          )
        ) {
          spreadsheetRow.require[
            "src.interfaceDefinition.inbound.inboundConverter.differentLayoutsForInputAndOutput"
          ] = RequireType.Mandatory;
        } else {
          spreadsheetRow.require[
            "src.interfaceDefinition.inbound.inboundConverter.differentLayoutsForInputAndOutput"
          ] = RequireType.NoRequire;
        }
        break;
      case "dst.interfaceDefinition.outbound.useConverter":
        if (
          ![InDefCommonFlagType.NO, null, ""].includes(
            spreadsheetRow.dst?.interfaceDefinition.outbound
              ?.useConverter as InDefCommonFlagType,
          )
        ) {
          spreadsheetRow.require[
            "dst.interfaceDefinition.outbound.outboundConverter.differentLayoutsForInputAndOutput"
          ] = RequireType.Mandatory;
        } else {
          spreadsheetRow.require[
            "dst.interfaceDefinition.outbound.outboundConverter.differentLayoutsForInputAndOutput"
          ] = RequireType.NoRequire;
        }
        break;

      case "dst.interfaceDefinition.outbound.protocol":
        if (
          spreadsheetRow.dst?.interfaceDefinition.outbound?.protocol ===
          EdhPrtclType.MSSP
        ) {
          spreadsheetRow.require[
            "dst.interfaceDefinition.outbound.autoRetryFlg"
          ] = RequireType.Mandatory;
        } else {
          spreadsheetRow.require[
            "dst.interfaceDefinition.outbound.autoRetryFlg"
          ] = RequireType.NoRequire;
        }
        break;

      case "src.interfaceDefinition.inbound.inboundMeta.ifCycle":
        if (
          [
            IfCycleType.EVERY_X_MINS,
            IfCycleType.DAILY_MULTIPLE,
            IfCycleType.MONTHLY,
            IfCycleType.MONTHLY_MULTIPLE,
            IfCycleType.WEEKLY,
            IfCycleType.WEEKLY_MULTIPLE,
            IfCycleType.QUARTERLY,
            IfCycleType.SEMI_ANNUAL,
            IfCycleType.ANNUAL,
            IfCycleType.MANUAL,
            IfCycleType.OTHER,
          ].includes(
            spreadsheetRow.src?.interfaceDefinition.inbound?.inboundMeta
              ?.ifCycle as IfCycleType,
          )
        ) {
          spreadsheetRow.require[
            "src.interfaceDefinition.inbound.inboundMeta.ifCycleNote"
          ] = RequireType.Mandatory;
        } else {
          spreadsheetRow.require[
            "src.interfaceDefinition.inbound.inboundMeta.ifCycleNote"
          ] = RequireType.Optional;
        }
        break;
    }

    return spreadsheetRow;
  };
  // DataKind input control by combining IF requirement types.
  const requireControllForDataKind = (spreadsheetRow: SpreadsheetRow) => {
    const targetItems: Array<keyof CellStyle> = [];
    targetItems.push(
      "project.dtKindIdIssueNumGrpCd",
      "src.interfaceDefinition.dataKindId",
      "src.interfaceDefinition.interfaceNameEnglish",
      "src.interfaceDefinition.interfaceNameJapanese",
      "project.mltcstDsbldFlg",
    );
    if (
      spreadsheetRow.src?.interfaceDefinition.interfaceRequirementType ===
        InterfaceRequirementType.CREATE &&
      spreadsheetRow.dst?.interfaceDefinition.interfaceRequirementType ===
        InterfaceRequirementType.CREATE
    ) {
      targetItems.forEach((item) => {
        spreadsheetRow.editable[item] = EditableType.Yes;
      });
    } else {
      targetItems.forEach((item) => {
        spreadsheetRow.editable[item] = EditableType.No;
      });
    }
  };

  const updateRecordSetDef = (
    spreadsheetRow: SpreadsheetRow,
    reqType: InterfaceRequirementType,
    srcDst: SrcDstType,
  ) => {
    if (reqType === InterfaceRequirementType.UPDATE) {
      if (srcDst === SrcDstType.Source) {
        spreadsheetRow.editable["src.interfaceDefinition.metaOnlyFlag"] =
          EditableType.Yes;
      } else {
        spreadsheetRow.editable["dst.interfaceDefinition.metaOnlyFlag"] =
          EditableType.Yes;
      }
    }
    cellItemsAll
      .filter((e) => recordSet[e].srcDst === srcDst)
      .forEach((e) => {
        spreadsheetRow.editable[e] = recordSet[e][reqType];
      });
    return spreadsheetRow;
  };

  const updateMetaOnlyEditable = (
    spreadsheetRow: SpreadsheetRow,
    srcDst: SrcDstType,
  ) => {
    cellItemsAll
      .filter((e) => recordSet[e].srcDst === srcDst)
      .forEach((e) => {
        if (!metaOnlyEditableItems.includes(e)) {
          spreadsheetRow.editable[e] = EditableType.No;
        }
      });
    return spreadsheetRow;
  };

  const getSpreadsheetRecordValue = (targetRow: any, name: string) => {
    const itemName = name.split(".");
    switch (itemName.length) {
      case 1:
        return targetRow[itemName[0]];
      case 2:
        return targetRow[itemName[0]][itemName[1]];
      case 3:
        return targetRow[itemName[0]][itemName[1]][itemName[2]];
      case 4:
        return targetRow[itemName[0]][itemName[1]][itemName[2]][itemName[3]];
      case 5:
        return targetRow[itemName[0]][itemName[1]][itemName[2]][itemName[3]][
          itemName[4]
        ];
      default:
        return "";
    }
  };

  const storeSpreadsheetRecord = (
    targetRow: any,
    name: string,
    value: string,
  ) => {
    const itemName = name.split(".");
    switch (itemName.length) {
      case 1:
        targetRow[itemName[0]] = value;
        break;
      case 2:
        targetRow[itemName[0]][itemName[1]] = value;
        break;
      case 3:
        targetRow[itemName[0]][itemName[1]][itemName[2]] = value;
        break;
      case 4:
        targetRow[itemName[0]][itemName[1]][itemName[2]][itemName[3]] = value;
        break;
      case 5:
        targetRow[itemName[0]][itemName[1]][itemName[2]][itemName[3]][
          itemName[4]
        ] = value;
        break;
    }
    return targetRow;
  };

  const clearItems = (targetRow: any, name: string, value: string) => {
    switch (name) {
      case "dst.interfaceDefinition.outbound.protocol":
        if (value !== EdhPrtclType.MSSP) {
          targetRow = storeSpreadsheetRecord(
            targetRow,
            "dst.interfaceDefinition.outbound.autoRetryFlg",
            "",
          );
          targetRow.updateColumn = setUpdateColumn(
            targetRow,
            "dst.interfaceDefinition.outbound.autoRetryFlg",
            "",
          );
        }

        break;
    }

    return targetRow;
  };

  const applyTagetRow = (targetRow: any, name: string, value: string) => {
    targetRow = storeSpreadsheetRecord(targetRow, name, value);
    targetRow = requireControll(name, targetRow);
    targetRow = clearItems(targetRow, name, value);
    targetRow = resolvePartnerType(targetRow, name, value);
  };

  const resetForChangeReqType = (
    spreadsheetRow: SpreadsheetRow,
    name: string,
    value: string,
  ) => {
    const srcDst =
      name === "src.interfaceDefinition.interfaceRequirementType"
        ? SrcDstType.Source
        : SrcDstType.Destination;

    switch (value) {
      case InterfaceRequirementType.CREATE:
        cellItemsAll
          .filter((e) => recordSet[e].srcDst === srcDst)
          .filter((item) => recordSet[item].C === EditableType.No)
          .forEach((item) => {
            spreadsheetRow = storeSpreadsheetRecord(spreadsheetRow, item, "");
          });
        break;
      case InterfaceRequirementType.UPDATE:
        cellItemsAll
          .filter((e) => recordSet[e].srcDst === srcDst)
          .filter((item) => recordSet[item].U === EditableType.No)
          .forEach((item) => {
            spreadsheetRow = storeSpreadsheetRecord(spreadsheetRow, item, "");
          });
        break;
      case InterfaceRequirementType.DELETE:
        cellItemsAll
          .filter((e) => recordSet[e].srcDst === srcDst)
          .filter((item) => recordSet[item].D === EditableType.No)
          .forEach((item) => {
            spreadsheetRow = storeSpreadsheetRecord(spreadsheetRow, item, "");
          });
        break;
      case InterfaceRequirementType.NO_CHANGE:
        cellItemsAll
          .filter((e) => recordSet[e].srcDst === srcDst)
          .filter((item) => recordSet[item].R === EditableType.No)
          .forEach((item) => {
            spreadsheetRow = storeSpreadsheetRecord(spreadsheetRow, item, "");
          });
        break;
    }

    spreadsheetRow.updateColumn = spreadsheetRow.updateColumn.filter((e) => {
      const item = e.key as keyof CellStyle;

      if (typeof recordSet[item] === "undefined") {
        return false;
      }

      if (recordSet[item].srcDst !== srcDst) return true;

      if (value === "C") {
        return recordSet[item].C === EditableType.Yes;
      }
      if (value === "U") {
        return recordSet[item].U === EditableType.Yes;
      }
      if (value === "D") {
        return recordSet[item].D === EditableType.Yes;
      }
      if (value === "R") {
        return recordSet[item].R === EditableType.Yes;
      }
      return false;
    });

    return spreadsheetRow;
  };

  const resolvePartnerType = (
    spreadsheetRow: SpreadsheetRow,
    name: string,
    value: string,
  ) => {
    const getMatchType = (cmpId: string) => {
      switch (cmpId) {
        case "WHS":
          return PtnrType.WAREHOUSE;
        case "ECWHS":
          return PtnrType.EC_WAREHOUSE;
        case "SUP":
          return PtnrType.SUPPLIER;
        case "CRR":
          return PtnrType.LOGISTICS;
        default:
          return "";
      }
    };

    const srcCmpPath = "src.interfaceDefinition.inbound.componentId";
    const srcPtnrPath =
      "src.interfaceDefinition.inbound.inboundPartner.partnerCode";
    const srcPtnrTypePath =
      "src.interfaceDefinition.inbound.inboundPartner.partnerType";
    const dstCmpPath = "dst.interfaceDefinition.outbound.componentId";
    const dstPtnrPath =
      "dst.interfaceDefinition.outbound.outboundPartner.partnerCode";
    const dstPtnrTypePath =
      "dst.interfaceDefinition.outbound.outboundPartner.partnerType";

    if (![srcCmpPath, srcPtnrPath, dstCmpPath, dstPtnrPath].includes(name)) {
      return spreadsheetRow;
    }

    const srcCmp = getSpreadsheetRecordValue(spreadsheetRow, srcCmpPath);
    const srcPtnr = getSpreadsheetRecordValue(spreadsheetRow, srcPtnrPath);
    const dstCmp = getSpreadsheetRecordValue(spreadsheetRow, dstCmpPath);
    const dstPtnr = getSpreadsheetRecordValue(spreadsheetRow, dstPtnrPath);

    switch (name) {
      case srcCmpPath:
        if (value && dstCmp === "SHP" && srcPtnr !== "") {
          const srcPtnrType = getMatchType(value);
          if (srcPtnrType) {
            spreadsheetRow = storeSpreadsheetRecord(
              spreadsheetRow,
              srcPtnrTypePath,
              srcPtnrType,
            );
            spreadsheetRow.updateColumn = setUpdateColumn(
              spreadsheetRow,
              srcPtnrTypePath,
              srcPtnrType,
            );
            spreadsheetRow = requireControll(srcPtnrTypePath, spreadsheetRow);
          }
        }
        if (value === "SHP" && dstCmp !== "" && dstPtnr !== "") {
          const dstPtnrType = getMatchType(dstCmp);
          if (dstPtnrType) {
            spreadsheetRow = storeSpreadsheetRecord(
              spreadsheetRow,
              dstPtnrTypePath,
              dstPtnrType,
            );
            spreadsheetRow.updateColumn = setUpdateColumn(
              spreadsheetRow,
              dstPtnrTypePath,
              dstPtnrType,
            );
            spreadsheetRow = requireControll(dstPtnrTypePath, spreadsheetRow);
          }
        }
        break;
      case srcPtnrPath:
        if (value && dstCmp === "SHP" && srcCmp !== "") {
          const srcPtnrType = getMatchType(srcCmp);
          if (srcPtnrType) {
            spreadsheetRow = storeSpreadsheetRecord(
              spreadsheetRow,
              srcPtnrTypePath,
              srcPtnrType,
            );
            spreadsheetRow.updateColumn = setUpdateColumn(
              spreadsheetRow,
              srcPtnrTypePath,
              srcPtnrType,
            );
            spreadsheetRow = requireControll(srcPtnrTypePath, spreadsheetRow);
          }
        }
        break;
      case dstCmpPath:
        if (value && srcCmp === "SHP" && dstPtnr !== "") {
          const dstPtnrType = getMatchType(value);
          if (dstPtnrType) {
            spreadsheetRow = storeSpreadsheetRecord(
              spreadsheetRow,
              dstPtnrTypePath,
              dstPtnrType,
            );
            spreadsheetRow.updateColumn = setUpdateColumn(
              spreadsheetRow,
              dstPtnrTypePath,
              dstPtnrType,
            );
            spreadsheetRow = requireControll(dstPtnrTypePath, spreadsheetRow);
          }
        }
        if (value === "SHP" && srcCmp !== "" && srcPtnr !== "") {
          const srcPtnrType = getMatchType(srcCmp);
          if (srcPtnrType) {
            spreadsheetRow = storeSpreadsheetRecord(
              spreadsheetRow,
              srcPtnrTypePath,
              srcPtnrType,
            );
            spreadsheetRow.updateColumn = setUpdateColumn(
              spreadsheetRow,
              srcPtnrTypePath,
              srcPtnrType,
            );
            spreadsheetRow = requireControll(srcPtnrTypePath, spreadsheetRow);
          }
        }
        break;
      case dstPtnrPath:
        if (value && srcCmp === "SHP" && dstCmp !== "") {
          const dstPtnrType = getMatchType(dstCmp);
          if (dstPtnrType) {
            spreadsheetRow = storeSpreadsheetRecord(
              spreadsheetRow,
              dstPtnrTypePath,
              dstPtnrType,
            );
            spreadsheetRow.updateColumn = setUpdateColumn(
              spreadsheetRow,
              dstPtnrTypePath,
              dstPtnrType,
            );
            spreadsheetRow = requireControll(dstPtnrTypePath, spreadsheetRow);
          }
        }
        break;
    }
    return spreadsheetRow;
  };

  const storeDataKindInfo = (targetRow: any, dataKindId: string) => {
    if (targetRow.src === undefined || targetRow.project === undefined)
      return targetRow;

    const idx = pageState.dataKind.dataKindList.findIndex(
      (e) => e.dtKindId === dataKindId,
    );

    targetRow.project.dtKindIdIssueNumGrpCd = "";
    targetRow.src.interfaceDefinition.interfaceNameEnglish =
      pageState.dataKind.dataKindList[idx].dtKindNameEng;
    targetRow.src.interfaceDefinition.interfaceNameJapanese =
      pageState.dataKind.dataKindList[idx].dtKindNameLclLang;
    targetRow.project.mltcstDsbldFlg =
      pageState.dataKind.dataKindList[idx].mltcstDsbldFlg;

    return targetRow;
  };

  const setUpdateColumn = (
    spreadsheetRow: SpreadsheetRow,
    name: string,
    value: string,
  ) => {
    const updateColumn = [{ key: name, value: value }]
      .concat(spreadsheetRow.updateColumn)
      .filter((x, i, arr) => arr.findIndex((y) => y.key === x.key) === i);

    if (name !== "src.interfaceDefinition.dataKindId") {
      return updateColumn;
    }
    return updateColumn
      .filter((e) => e.key !== "src.interfaceDefinition.interfaceNameEnglish")
      .filter((e) => e.key !== "src.interfaceDefinition.interfaceNameJapanese")
      .filter((e) => e.key !== "project.mltcstDsbldFlg");
  };

  const spreadsheetInitialData = useMemo(() => {
    if (pageState.result === undefined) return;

    const isChanging =
      pageState.project?.executionStatus.stsId ===
      TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS;

    const spreadsheetRow: SpreadsheetRow[] = pageState.result.ifList.map(
      (e) => {
        let row: SpreadsheetRow = {
          ...JSON.parse(
            JSON.stringify(
              InterfaceRequirementListPageInitialState.spreadsheet.tmplateRow,
            ),
          ),
          // Gray out cancelled IF requirement
          cancelledRequirement:
            (e.projectTcktInfo?.srcDsgnWFlwStsDsplyName ==
              DSGN_W_FLW_STS_CANCELLED &&
              e.projectTcktInfo?.dstDsgnWFlwStsDsplyName ==
                DSGN_W_FLW_STS_CANCELLED) ||
            ([DPlYMNT_W_FLW_STS_CANCELLED, DPlYMNT_W_FLW_STS_DEPLOYED].includes(
              e.projectTcktInfo?.srcStgDplymntWFlwStsDsplyName,
            ) &&
              [
                DPlYMNT_W_FLW_STS_CANCELLED,
                DPlYMNT_W_FLW_STS_DEPLOYED,
              ].includes(e.projectTcktInfo?.dstStgDplymntWFlwStsDsplyName) &&
              e.projectTcktInfo?.srcPrdDplymntWFlwStsDsplyName ==
                DPlYMNT_W_FLW_STS_CANCELLED)
              ? true
              : false,
          updateDateTime: e.updateDateTime,
          project: e.project,
          src: e.src,
          dst: e.dst,
          information: e.information,
          projectTcktInfo: e.projectTcktInfo,
        };
        cellItemsAll.forEach((col) => {
          row = requireControll(col, row);
        });
        if (
          !["", null, undefined].includes(
            row.src?.interfaceDefinition.dataKindId,
          ) &&
          pageState.dataKind.dataKindList.some(
            (e) => e.dtKindId === row.src?.interfaceDefinition.dataKindId,
          )
        ) {
          row = storeDataKindInfo(
            row,
            row.src?.interfaceDefinition.dataKindId ?? "",
          );
        }

        // Fix can not change value after saved.
        if (row.project?.ifReqId !== "") {
          row.require["src.interfaceDefinition.interfaceRequirementType"] =
            RequireType.NoRequire;
          row.require["src.interfaceDefinition.metaOnlyFlag"] =
            RequireType.NoRequire;
          row.require["dst.interfaceDefinition.metaOnlyFlag"] =
            RequireType.NoRequire;
          row.require["src.interfaceDefinition.dipByRegion"] =
            RequireType.NoRequire;
          row.require["dst.interfaceDefinition.interfaceRequirementType"] =
            RequireType.NoRequire;
          row.require["dst.interfaceDefinition.dipByRegion"] =
            RequireType.NoRequire;
          row.require["project.exgInbdId"] = RequireType.NoRequire;
          row.require["project.exgOtbdId"] = RequireType.NoRequire;
        }

        // Fix can not change value after determined.
        if (isChanging && row.projectTcktInfo?.srcDsgnTcktId) {
          row.require["src.interfaceDefinition.dataKindId"] =
            RequireType.NoRequire;
          row.require["src.interfaceDefinition.inbound.componentId"] =
            RequireType.NoRequire;
          row.require["src.interfaceDefinition.inbound.inboundIdOptionalPart"] =
            RequireType.NoRequire;
          row.require[
            "src.interfaceDefinition.inbound.inboundPartner.partnerType"
          ] = RequireType.NoRequire;
          row.require[
            "src.interfaceDefinition.inbound.inboundPartner.partnerCode"
          ] = RequireType.NoRequire;
          row.require[
            "src.interfaceDefinition.inbound.inboundPartner.partnerBusiness"
          ] = RequireType.NoRequire;
          row.require[
            "src.interfaceDefinition.outbound.outboundIdOptionalPart"
          ] = RequireType.NoRequire;
          row.require["dst.interfaceDefinition.outbound.componentId"] =
            RequireType.NoRequire;
          row.require[
            "dst.interfaceDefinition.outbound.outboundIdOptionalPart"
          ] = RequireType.NoRequire;
          row.require[
            "dst.interfaceDefinition.outbound.outboundPartner.partnerType"
          ] = RequireType.NoRequire;
          row.require[
            "dst.interfaceDefinition.outbound.outboundPartner.partnerCode"
          ] = RequireType.NoRequire;
          row.require[
            "dst.interfaceDefinition.outbound.outboundPartner.partnerBusiness"
          ] = RequireType.NoRequire;
        }
        return row;
      },
    );

    const ifReqCnclTypeCdByStats = ![
      TcktStsTypePrjPrgrs.WAITING,
      TcktStsTypePrjPrgrs.ASSIGNING_STAKEHOLDERS,
      TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
    ].includes(pageState.project?.executionStatus.stsId as TcktStsTypePrjPrgrs)
      ? pageState.codes.ifReqCnclType
      : pageState.codes.ifReqCnclType?.filter((e) =>
          [IfReqCnclType.NORMAL, IfReqCnclType.CANCEL].includes(
            e.value as IfReqCnclType,
          ),
        );

    return {
      results: spreadsheetRow,
      mode: {
        readOnly:
          !pageState.loginUserRoleList
            .map((e) => e.roleCd)
            .includes(RoleType.PIC_OF_IF_RD) ||
          ![
            TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
            TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
          ].includes(
            pageState.project?.executionStatus.stsId as TcktStsTypePrjPrgrs,
          ),
        isChanging:
          pageState.project?.executionStatus.stsId ===
          TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
      },
      codes: {
        inDefCommonFlagType: pageState.codes.inDefCommonFlagType,
        ptnrType: pageState.codes.ptnrType,
        interfaceRequirementType: pageState.codes.interfaceRequirementType,
        securityInformationLevelType:
          pageState.codes.securityInformationLevelType,
        encriptionType: pageState.codes.encriptionType,
        snapshotIncrementalDeltaType:
          pageState.codes.snapshotIncrementalDeltaType,
        ifCycleType: pageState.codes.ifCycleType,
        dataScopeType: pageState.codes.dataScopeType,
        fileTypeType: pageState.codes.fileTypeType,
        ediPollingAdaptorType: pageState.codes.ediPollingAdaptorType,
        sendDatalakeType: pageState.codes.sendDatalakeType,
        ifReqCnclType: ifReqCnclTypeCdByStats,
        dipByRegionList: pageState.codes.dipByRegionList,
        inboundEdhProtocolList: pageState.codes.inboundEdhProtocolList,
        outboundEdhProtocolList: pageState.codes.outboundEdhProtocolList,
        srcEdiChainTypeList: pageState.codes.srcEdiChainTypeList,
        dstEdiChainTypeList: pageState.codes.dstEdiChainTypeList,
      },
    };
  }, [
    pageState.result,
    pageState.codes,
    pageState.dataKind,
    pageState.project,
    pageState.loginUserRoleList,
  ]);

  const onCellEditedHandle = (event: CustomEvent) => {
    const rowIdx: number = event.detail.cell.data.index;
    const value: string = event.detail.cell.value ?? "";
    const isChangedRow: boolean = event.detail.cell._row.isChanged;
    const name: string = event.detail.name ?? "";
    const isValid = event.detail.cell.valid;
    const cellItem: CellItem = {
      rowIdx: rowIdx,
      col: name,
    };
    const newSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    let targetRow = JSON.parse(JSON.stringify(newSpreadsheetRow[rowIdx]));
    if (
      targetRow.editable[name] === EditableType.No ||
      targetRow.require[name] === RequireType.NoRequire
    )
      return;

    const updateSpreadsheetState = (waitRef: boolean) => {
      applyTagetRow(targetRow, name, value);
      targetRow.updateFlag = isChangedRow;
      if (isChangedRow) {
        targetRow.updateColumn = setUpdateColumn(targetRow, name, value);
      }
      newSpreadsheetRow[rowIdx] = targetRow;

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRow],
      });

      setPageState((prevState) => ({
        ...prevState,
        status: {
          ...prevState.status,
          invalidRecordList: waitRef
            ? prevState.status.invalidRecordList
            : isValid
              ? prevState.status.invalidRecordList.filter(
                  (e) => e !== JSON.stringify(cellItem),
                )
              : prevState.status.invalidRecordList.concat(
                  JSON.stringify(cellItem),
                ),
        },
      }));
    };

    const updateSpreadsheetStateWithReference = () => {
      if (name === "src.interfaceDefinition.dataKindId") {
        if (
          !pageState.dataKind.dataKindList.some((e) => e.dtKindId === value)
        ) {
          showToast({
            design: "alert",
            message: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00125,
              "Specified ID",
            ),
          });
          return;
        }
        targetRow = storeDataKindInfo(targetRow, value);
        updateSpreadsheetState(false);
      } else if (name === "project.exgInbdId") {
        getIfDefinitionForReferenceRequest({
          dfnSetVerNum: dfnSetVerNum,
          bdryIdList: [
            {
              idx: rowIdx,
              bdryId: value,
              bdryType: BdryType.INBOUND,
              dstDipByRgn: targetRow.project.exgOtbdId
                ? null
                : targetRow.dst.interfaceDefinition.dipByRegion,
              dstOtbdId: targetRow.project.exgOtbdId,
            },
          ],
        });
        updateSpreadsheetState(true);
      } else if (name === "dst.interfaceDefinition.dipByRegion") {
        getIfDefinitionForReferenceRequest({
          dfnSetVerNum: dfnSetVerNum,
          bdryIdList: [
            {
              idx: rowIdx,
              bdryId: targetRow.project.exgInbdId,
              bdryType: BdryType.INBOUND,
              dstDipByRgn: value,
            },
          ],
        });
        updateSpreadsheetState(true);
      } else if (name === "project.exgOtbdId") {
        getIfDefinitionForReferenceRequest({
          dfnSetVerNum: dfnSetVerNum,
          bdryIdList: [
            {
              idx: rowIdx,
              bdryId: value,
              bdryType: BdryType.OUTBOUND,
              srcInbdId: targetRow.project.exgInbdId,
            },
          ],
        });
        updateSpreadsheetState(true);
      }
    };

    if (
      [
        "src.interfaceDefinition.interfaceRequirementType",
        "dst.interfaceDefinition.interfaceRequirementType",
      ].includes(name) &&
      spreadsheetState?.results[rowIdx].updateColumn
        .map((e) => e.key)
        .includes(name)
    ) {
      targetRow = resetForChangeReqType(targetRow, name, value);
      updateSpreadsheetState(false);
    } else if (
      (["src.interfaceDefinition.dataKindId"].includes(name) && value !== "") ||
      (name === "project.exgInbdId" && value !== "") ||
      (name === "dst.interfaceDefinition.dipByRegion" &&
        value !== "" &&
        targetRow.project.exgInbdId != "") ||
      (name === "project.exgOtbdId" && value !== "")
    ) {
      updateSpreadsheetStateWithReference();
    } else {
      updateSpreadsheetState(false);
    }
  };

  const onCellBulkEditedHandle = (event: CustomEvent) => {
    const newSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];
    let invalidRecordList = pageState.status.invalidRecordList;
    const ifRefBybulkEditOfInbdIDList: BoundaryId[] = [];
    const ifRefBybulkEditOfOutbdIDList: BoundaryId[] = [];
    const ifRefBybulkEditOfDipList: BoundaryId[] = [];

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const rowIdx: number = item.data.index;
      const value: string = item.value ?? "";
      const isChangedRow: boolean = item.isChanged;
      const name: string = event.detail.name ?? "";
      const isValid = item.valid;
      const cellItem: CellItem = {
        rowIdx: rowIdx,
        col: name,
      };
      let targetRow = JSON.parse(JSON.stringify(newSpreadsheetRow[rowIdx]));
      if (
        targetRow.editable[name] === EditableType.No ||
        targetRow.require[name] === RequireType.NoRequire
      )
        return;

      const updateSpreadsheetState = (waitRef: boolean) => {
        applyTagetRow(targetRow, name, value);
        targetRow.updateFlag = isChangedRow;
        if (isChangedRow) {
          targetRow.updateColumn = setUpdateColumn(targetRow, name, value);
        }
        newSpreadsheetRow[rowIdx] = targetRow;
        invalidRecordList = waitRef
          ? pageState.status.invalidRecordList
          : isValid
            ? pageState.status.invalidRecordList.filter(
                (e) => e !== JSON.stringify(cellItem),
              )
            : pageState.status.invalidRecordList.concat(
                JSON.stringify(cellItem),
              );
      };

      const updateSpreadsheetStateWithReference = () => {
        if (name === "src.interfaceDefinition.dataKindId") {
          if (
            !pageState.dataKind.dataKindList.some((e) => e.dtKindId === value)
          ) {
            showToast({
              design: "alert",
              message: messageForScreen(
                ApplicationMessageScreen.APP_SCN_00125,
                "Specified ID",
              ),
            });
            return;
          }
          targetRow = storeDataKindInfo(targetRow, value);
          updateSpreadsheetState(false);
        } else if (name === "project.exgInbdId") {
          ifRefBybulkEditOfInbdIDList.push({
            idx: rowIdx,
            bdryId: value,
            bdryType: BdryType.INBOUND,
            dstDipByRgn: targetRow.project.exgOtbdId
              ? null
              : targetRow.dst.interfaceDefinition.dipByRegion,
            dstOtbdId: targetRow.project.exgOtbdId,
          });
          updateSpreadsheetState(true);
        } else if (name === "dst.interfaceDefinition.dipByRegion") {
          ifRefBybulkEditOfDipList.push({
            idx: rowIdx,
            bdryId: targetRow.project.exgInbdId,
            bdryType: BdryType.INBOUND,
            dstDipByRgn: value,
          });
          updateSpreadsheetState(true);
        } else if (name === "project.exgOtbdId") {
          ifRefBybulkEditOfOutbdIDList.push({
            idx: rowIdx,
            bdryId: value,
            bdryType: BdryType.OUTBOUND,
            srcInbdId: targetRow.project.exgInbdId,
          });
          updateSpreadsheetState(true);
        }
      };
      if (
        [
          "src.interfaceDefinition.interfaceRequirementType",
          "dst.interfaceDefinition.interfaceRequirementType",
        ].includes(name) &&
        spreadsheetState?.results[rowIdx].updateColumn
          .map((e) => e.key)
          .includes(name)
      ) {
        targetRow = resetForChangeReqType(targetRow, name, value);
        updateSpreadsheetState(false);
      } else if (
        (["src.interfaceDefinition.dataKindId"].includes(name) &&
          value !== "") ||
        (name === "project.exgInbdId" && value !== "") ||
        (name === "dst.interfaceDefinition.dipByRegion" &&
          value !== "" &&
          targetRow.project.exgInbdId != "") ||
        (name === "project.exgOtbdId" && value !== "")
      ) {
        updateSpreadsheetStateWithReference();
      } else {
        // Update the value of items.
        updateSpreadsheetState(false);
      }
    });
    // To receive search results for InboundID, OutboundID and DipByRgn respectively.
    if (ifRefBybulkEditOfInbdIDList.length > 0) {
      getIfRefBybulkEditOfInbdIDRequest({
        dfnSetVerNum: dfnSetVerNum,
        bdryIdList: ifRefBybulkEditOfInbdIDList,
      });
    }
    if (ifRefBybulkEditOfOutbdIDList.length > 0) {
      getIfRefBybulkEditOfOutbdIDRequest({
        dfnSetVerNum: dfnSetVerNum,
        bdryIdList: ifRefBybulkEditOfOutbdIDList,
      });
    }
    if (ifRefBybulkEditOfDipList.length > 0) {
      getIfRefBybulkEditOfDipRequest({
        dfnSetVerNum: dfnSetVerNum,
        bdryIdList: ifRefBybulkEditOfDipList,
      });
    }
    // Set Updated items.
    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRow],
    });

    setPageState((prevState) => ({
      ...prevState,
      status: {
        ...prevState.status,
        invalidRecordList: invalidRecordList,
      },
    }));
  };

  const onCellClickedHandle = (event: CustomEvent) => {
    const rowIdx: number = event.detail.cell.data.index;
    const name = event.detail.name;

    if (name !== "deleteButton") return;
    const newSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    newSpreadsheetRow.splice(rowIdx, 1);
    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRow],
    });
  };

  const onCellSelectedHandle = (event: CustomEvent) => {
    const rowIndex: number = event.detail.cell?.data.index;
    setPageState((prevState) => ({
      ...prevState,
      spreadsheet: {
        ...prevState.spreadsheet,
        selectRowIdx: rowIndex,
      },
    }));
  };

  const onClickLoad = async (event: CustomEvent): Promise<void> => {
    const file = event.detail.file as File;
    let loadedRequirementList: CellStyle[] = [];
    let extractloadedRequirementList: CellStyle[] = [];

    const newSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
      ? [...spreadsheetState.results.filter((e) => !e.isAddRow)]
      : [];

    try {
      const data: any = await new SpreadsheetTemplate(
        TEMPLATE_FILE_URL,
      ).loadDataFile(file);
      loadedRequirementList = data;
    } catch (e) {
      console.log(e);
    }

    const dropdownCellList = new Map<string, Option[] | undefined>([
      ["project.ifReqCnclTypeCd", pageState.codes.ifReqCnclType],
      [
        "src.interfaceDefinition.interfaceRequirementType",
        pageState.codes.interfaceRequirementType,
      ],
      [
        "dst.interfaceDefinition.interfaceRequirementType",
        pageState.codes.interfaceRequirementType,
      ],
      ["project.mltcstDsbldFlg", pageState.codes.inDefCommonFlagType],
      ["src.interfaceDefinition.dipByRegion", pageState.codes.dipByRegionList],
      [
        "src.interfaceDefinition.inbound.inboundPartner.partnerType",
        pageState.codes.ptnrType,
      ],
      ["dst.interfaceDefinition.dipByRegion", pageState.codes.dipByRegionList],
      [
        "dst.interfaceDefinition.outbound.outboundPartner.partnerType",
        pageState.codes.ptnrType,
      ],
      [
        "src.interfaceDefinition.inbound.securityInformationLevel",
        pageState.codes.securityInformationLevelType,
      ],
      [
        "src.interfaceDefinition.inbound.inboundSecurity.inboundEncryptionTypeValue",
        pageState.codes.encriptionType,
      ],
      [
        "dst.interfaceDefinition.outbound.outboundSecurity.outboundEncryptionTypeValue",
        pageState.codes.encriptionType,
      ],
      [
        "src.interfaceDefinition.inbound.inboundMeta.ifCycle",
        pageState.codes.ifCycleType,
      ],
      [
        "src.interfaceDefinition.inbound.inboundMeta.snapshotIncrementalDelta",
        pageState.codes.snapshotIncrementalDeltaType,
      ],
      [
        "src.interfaceDefinition.inbound.inboundMeta.dataScopePerInterfaceId",
        pageState.codes.dataScopeType,
      ],
      [
        "src.interfaceDefinition.inbound.inboundMeta.dataScopePerInterfaceFile",
        pageState.codes.dataScopeType,
      ],
      [
        "src.interfaceDefinition.inbound.protocol",
        pageState.codes.inboundEdhProtocolList,
      ],
      [
        "src.interfaceDefinition.inbound.useEtlTransceiver",
        pageState.codes.inDefCommonFlagType,
      ],
      [
        "src.interfaceDefinition.inbound.useConverter",
        pageState.codes.inDefCommonFlagType,
      ],
      [
        "src.interfaceDefinition.inbound.inboundConverter.differentLayoutsForInputAndOutput",
        pageState.codes.inDefCommonFlagType,
      ],
      [
        "src.interfaceDefinition.inbound.inboundPartner.chainTypeCode",
        pageState.codes.srcEdiChainTypeList,
      ],
      [
        "src.interfaceDefinition.inbound.inboundPartner.fileTypeCode",
        pageState.codes.fileTypeType,
      ],
      [
        "src.interfaceDefinition.inbound.inboundPartner.pollingAdaptorTypeCode",
        pageState.codes.ediPollingAdaptorType,
      ],
      [
        "dst.interfaceDefinition.outbound.outboundMeta.dataScopePerInterfaceId",
        pageState.codes.dataScopeType,
      ],
      [
        "dst.interfaceDefinition.outbound.outboundMeta.dataScopePerInterfaceFile",
        pageState.codes.dataScopeType,
      ],
      [
        "dst.interfaceDefinition.outbound.protocol",
        pageState.codes.outboundEdhProtocolList,
      ],
      [
        "dst.interfaceDefinition.outbound.sendDatalakeType",
        pageState.codes.sendDatalakeType,
      ],
      [
        "dst.interfaceDefinition.outbound.useEtlTransceiver",
        pageState.codes.inDefCommonFlagType,
      ],
      [
        "dst.interfaceDefinition.outbound.useConverter",
        pageState.codes.inDefCommonFlagType,
      ],
      [
        "dst.interfaceDefinition.outbound.outboundConverter.differentLayoutsForInputAndOutput",
        pageState.codes.inDefCommonFlagType,
      ],
      [
        "dst.interfaceDefinition.outbound.outboundPartner.chainTypeCode",
        pageState.codes.dstEdiChainTypeList,
      ],
      [
        "dst.interfaceDefinition.outbound.outboundPartner.fileTypeCode",
        pageState.codes.fileTypeType,
      ],
      [
        "dst.interfaceDefinition.outbound.autoRetryFlg",
        pageState.codes.inDefCommonFlagType,
      ],
      [
        "src.interfaceDefinition.metaOnlyFlag",
        pageState.codes.inDefCommonFlagType,
      ],
      [
        "dst.interfaceDefinition.metaOnlyFlag",
        pageState.codes.inDefCommonFlagType,
      ],
    ]);

    const blankRowFilterdList = loadedRequirementList.filter((row) =>
      cellItemsAll.some((e) => row[e] !== ""),
    );
    blankRowFilterdList.forEach((loadedRow) => {
      cellItemsAll.forEach((item) => {
        if (dropdownCellList.has(item)) {
          loadedRow[item] =
            dropdownCellList.get(item)?.find((e) => loadedRow[item] === e.label)
              ?.value ?? "";
        }
      });
      extractloadedRequirementList.push(loadedRow);
    });

    if (extractloadedRequirementList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(
          ApplicationMessageScreen.APP_SCN_00125,
          "Data",
        ),
      });
      return;
    }

    setPageState((prevState) => ({
      ...prevState,
      status: {
        ...prevState.status,
        loadedCount: extractloadedRequirementList.length,
      },
    }));

    // Change Delete Removed Row.
    newSpreadsheetRow
      .filter(
        (row) =>
          !extractloadedRequirementList
            .filter((e) => e["project.ifReqId"] !== "")
            .map((e) => e["project.ifReqId"])
            .includes(row.project?.ifReqId ?? ""),
      )
      .forEach((deleteRow) => {
        if (
          deleteRow.project !== undefined &&
          deleteRow.project?.ifReqId !== ""
        ) {
          deleteRow.project.ifReqCnclTypeCd = IfReqCnclType.CANCEL;
          deleteRow.updateFlag = true;
        }
      });

    const setRequireReferenceDataKindInfo = (targetRow: SpreadsheetRow) => {
      const dataKindId = getSpreadsheetRecordValue(
        targetRow,
        "src.interfaceDefinition.dataKindId",
      );
      if (["", null].includes(dataKindId)) {
        return targetRow;
      }
      if (
        !pageState.dataKind.dataKindList.some((e) => e.dtKindId === dataKindId)
      ) {
        showToast({
          design: "alert",
          message: messageForScreen(
            ApplicationMessageScreen.APP_SCN_00125,
            "Specified ID",
          ),
        });
        return storeSpreadsheetRecord(
          targetRow,
          "src.interfaceDefinition.dataKindId",
          "",
        );
      }
      return storeDataKindInfo(targetRow, dataKindId);
    };

    const requireReferenceInboundIdList: BoundaryId[] = [];
    const requireReferenceOutboundIdList: BoundaryId[] = [];
    const setRequireRegferenceList = (
      rowIndex: number,
      targetRow: SpreadsheetRow,
    ) => {
      if (
        targetRow.updateColumn.map((e) => e.key).includes("project.exgInbdId")
      ) {
        const exgOtbdId = getSpreadsheetRecordValue(
          targetRow,
          "project.exgOtbdId",
        );
        requireReferenceInboundIdList.push({
          idx: rowIndex,
          bdryId: getSpreadsheetRecordValue(targetRow, "project.exgInbdId"),
          bdryType: BdryType.INBOUND,
          dstDipByRgn: exgOtbdId
            ? null
            : getSpreadsheetRecordValue(
                targetRow,
                "dst.interfaceDefinition.dipByRegion",
              ),
          dstOtbdId: exgOtbdId,
        });
      }
      if (
        targetRow.updateColumn.map((e) => e.key).includes("project.exgOtbdId")
      ) {
        requireReferenceOutboundIdList.push({
          idx: rowIndex,
          bdryId: getSpreadsheetRecordValue(targetRow, "project.exgOtbdId"),
          bdryType: BdryType.OUTBOUND,
          srcInbdId: getSpreadsheetRecordValue(targetRow, "project.exgInbdId"),
        });
      }
    };

    // Update and Add loaded Row.
    extractloadedRequirementList.forEach((loadedRow) => {
      if (
        loadedRow["project.ifReqId"] !== "" &&
        newSpreadsheetRow.some(
          (e) => e.project?.ifReqId === loadedRow["project.ifReqId"],
        )
      ) {
        const rowIdx = newSpreadsheetRow.findIndex(
          (e) => e.project?.ifReqId === loadedRow["project.ifReqId"],
        );
        let targetRow = JSON.parse(JSON.stringify(newSpreadsheetRow[rowIdx]));
        const targetRowOrg = JSON.parse(
          JSON.stringify(newSpreadsheetRow[rowIdx]),
        );
        cellItemsAll
          .filter(
            (item) =>
              ![
                "src.interfaceDefinition.interfaceRequirementType",
                "dst.interfaceDefinition.interfaceRequirementType",
              ].includes(item),
          )
          .filter(
            (item) =>
              loadedRow[item] !== getSpreadsheetRecordValue(targetRowOrg, item),
          )
          .filter(
            (item) =>
              loadedRow[item] !== "" ||
              getSpreadsheetRecordValue(targetRowOrg, item) !== null,
          )
          .forEach((item) => {
            applyTagetRow(targetRow, item, loadedRow[item]);
          });

        cellItemsAll
          .filter(
            (item) =>
              loadedRow[item] !== getSpreadsheetRecordValue(targetRowOrg, item),
          )
          .filter(
            (item) =>
              loadedRow[item] !== "" ||
              getSpreadsheetRecordValue(targetRowOrg, item) !== null,
          )
          .forEach((item) => {
            if (
              targetRow.editable[item] !== EditableType.No &&
              targetRow.require[item] !== RequireType.NoRequire
            ) {
              targetRow.updateColumn = setUpdateColumn(
                targetRow,
                item,
                loadedRow[item],
              );
            } else {
              targetRow = storeSpreadsheetRecord(
                targetRow,
                item,
                getSpreadsheetRecordValue(targetRowOrg, item),
              );
            }
          });
        if (targetRow.updateColumn.length > 0) {
          targetRow.updateFlag = true;
          targetRow = setRequireReferenceDataKindInfo(targetRow);
        }
        newSpreadsheetRow[rowIdx] = targetRow;
      } else {
        let newRow = JSON.parse(
          JSON.stringify(
            InterfaceRequirementListPageInitialState.spreadsheet.tmplateRow,
          ),
        );
        newRow.isAddRow = true;
        newRow.updateFlag = true;
        cellItemsAll
          .filter((item) => loadedRow[item] !== "")
          .forEach((item) => {
            applyTagetRow(newRow, item, loadedRow[item]);
          });
        cellItemsAll
          .filter((item) => loadedRow[item] !== "")
          .forEach((item) => {
            if (
              newRow.editable[item] !== EditableType.No &&
              newRow.require[item] !== RequireType.NoRequire
            ) {
              newRow.updateColumn = setUpdateColumn(
                newRow,
                item,
                loadedRow[item],
              );
            } else {
              newRow = storeSpreadsheetRecord(newRow, item, "");
            }
          });
        newRow = setRequireReferenceDataKindInfo(newRow);
        const rowIndex = newSpreadsheetRow.length;
        setRequireRegferenceList(rowIndex, newRow);
        newSpreadsheetRow.splice(rowIndex, 0, newRow);
      }
    });

    const bdryIdList: BoundaryId[] = requireReferenceInboundIdList.concat(
      requireReferenceOutboundIdList,
    );
    getIfDefinitionForReferenceRequest({
      dfnSetVerNum: dfnSetVerNum,
      bdryIdList: bdryIdList,
    });

    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRow],
    });
  };

  const initializeSpreadSheetRowEditable = (row: SpreadsheetRow) => {
    cellItemsAll.forEach((e) => {
      row.editable[e] = recordSet[e].INIT;
    });
  };

  const onClickAddRow = () => {
    const insertRowIndex: number =
      (pageState.spreadsheet.selectRowIdx ?? -1) + 1;

    const newSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
      ? [...spreadsheetState.results]
      : [];

    let addSpreadsheetRow: SpreadsheetRow = JSON.parse(
      JSON.stringify(
        InterfaceRequirementListPageInitialState.spreadsheet.tmplateRow,
      ),
    );
    addSpreadsheetRow.isAddRow = true;
    addSpreadsheetRow.updateFlag = true;
    addSpreadsheetRow.updateColumn = [
      {
        key: "project.ifReqCnclTypeCd",
        value: IfReqCnclType.NORMAL,
      },
    ];

    initializeSpreadSheetRowEditable(addSpreadsheetRow);

    newSpreadsheetRow.splice(insertRowIndex, 0, addSpreadsheetRow);
    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRow],
    });
  };

  const onClickDownload = () => {
    resetUserError();

    const request = {
      type: REPORT_TYPE_IF_REQUIREMENT,
      dfnSetVerNum: dfnSetVerNum,
      pjtId: params.pjtId,
    };
    ifDefinitionReportRequest(request);
  };

  const onClickValidate = () => {
    resetUserError();

    const request = {
      dfnSetVerNum: dfnSetVerNum,
      validationTypeCode:
        ValidationTypeCode.FINALIZE_REQUIREMENTS_PRJ_PLAN_SCREEN,
      pjtId: params.pjtId,
    };
    getIfDefinitionCommonValidationResultRequest(request);
  };

  const onListenGetIfDefinitionCommonValidationResultResponse = (): void => {
    if (getIfDefinitionCommonValidationResultResponse === undefined) return;

    // 400 response
    if (
      Array.isArray(getIfDefinitionCommonValidationResultResponse) &&
      getIfDefinitionCommonValidationResultResponse.length > 0
    ) {
      const currentSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
        ? [...spreadsheetState.results]
        : [];

      const newSpreadsheetRow = mappingErrorResponseToSpreadsheet(
        getIfDefinitionCommonValidationResultResponse,
        currentSpreadsheetRow,
        InterfaceRequirementListPageInitialState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRow],
      });
      return;
    }

    // 200 success response with message.
    if (
      Array.isArray(getIfDefinitionCommonValidationResultResponse.result) &&
      getIfDefinitionCommonValidationResultResponse.result.length > 0
    ) {
      showToast({
        design: "alert",
        message: "Validation errors found.",
      });

      const currentSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
        ? [...spreadsheetState.results]
        : [];

      const indexMappedResponseResult =
        getIfDefinitionCommonValidationResultResponse.result.map((e: any) => ({
          ...e,
          index: currentSpreadsheetRow.findIndex(
            (s) => s.project?.ifReqId === e.ifReqId,
          ),
        }));

      const newSpreadsheetRow = mappingErrorResponseToSpreadsheet(
        indexMappedResponseResult,
        currentSpreadsheetRow,
        InterfaceRequirementListPageInitialState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRow],
      });
      return;
    }

    showToast({
      design: "completion",
      message: "No validation errors found.",
    });
  };
  useEffect(onListenGetIfDefinitionCommonValidationResultResponse, [
    getIfDefinitionCommonValidationResultResponse,
  ]);

  const onClickProjectPlanPage = () => {
    if (!spreadsheetState) return;

    if (!spreadsheetState?.results.some((e) => e.updateFlag)) {
      resetUserError();
      navigate(`/project-plan?pjtId=${params.pjtId}`);
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: () => {
          resetUserError();
          navigate(`/project-plan?pjtId=${params.pjtId}`);
        },
      });
    }
  };

  const onClickReload = () => {
    if (!spreadsheetState) return;

    if (!spreadsheetState?.results.some((e) => e.updateFlag)) {
      resetUserError();
      onInitialDisplayed();
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: () => {
          resetUserError();
          onInitialDisplayed();
        },
      });
    }
  };

  const storeTargetColumn = (targetRow: any, name: string, value: string) => {
    const itemName = name.split(".");
    switch (itemName.length) {
      case 1:
        targetRow[itemName[0]] = value;
        break;
      case 2:
        if (!targetRow.hasOwnProperty(itemName[0])) {
          targetRow[itemName[0]] = { [itemName[1]]: value };
        } else {
          targetRow[itemName[0]][itemName[1]] = value;
        }
        break;
      case 3:
        if (!targetRow.hasOwnProperty(itemName[0])) {
          targetRow[itemName[0]] = { [itemName[1]]: { [itemName[2]]: value } };
        } else if (!targetRow[itemName[0]].hasOwnProperty(itemName[1])) {
          targetRow[itemName[0]][itemName[1]] = { [itemName[2]]: value };
        } else {
          targetRow[itemName[0]][itemName[1]][itemName[2]] = value;
        }
        break;
      case 4:
        if (!targetRow.hasOwnProperty(itemName[0])) {
          targetRow[itemName[0]] = {
            [itemName[1]]: { [itemName[2]]: { [itemName[3]]: value } },
          };
        } else if (!targetRow[itemName[0]].hasOwnProperty(itemName[1])) {
          targetRow[itemName[0]][itemName[1]] = {
            [itemName[2]]: { [itemName[3]]: value },
          };
        } else if (
          !targetRow[itemName[0]][itemName[1]].hasOwnProperty(itemName[2])
        ) {
          targetRow[itemName[0]][itemName[1]][itemName[2]] = {
            [itemName[3]]: value,
          };
        } else {
          targetRow[itemName[0]][itemName[1]][itemName[2]][itemName[3]] = value;
        }
        break;
      case 5:
        if (!targetRow.hasOwnProperty(itemName[0])) {
          targetRow[itemName[0]] = {
            [itemName[1]]: {
              [itemName[2]]: { [itemName[3]]: { [itemName[4]]: value } },
            },
          };
        } else if (!targetRow[itemName[0]].hasOwnProperty(itemName[1])) {
          targetRow[itemName[0]][itemName[1]] = {
            [itemName[2]]: { [itemName[3]]: { [itemName[4]]: value } },
          };
        } else if (
          !targetRow[itemName[0]][itemName[1]].hasOwnProperty(itemName[2])
        ) {
          targetRow[itemName[0]][itemName[1]][itemName[2]] = {
            [itemName[3]]: { [itemName[4]]: value },
          };
        } else if (
          !targetRow[itemName[0]][itemName[1]][itemName[2]].hasOwnProperty(
            itemName[3],
          )
        ) {
          targetRow[itemName[0]][itemName[1]][itemName[2]][itemName[3]] = {
            [itemName[4]]: value,
          };
        } else {
          targetRow[itemName[0]][itemName[1]][itemName[2]][itemName[3]][
            itemName[4]
          ] = value;
        }
        break;
    }
    return targetRow;
  };

  const onClickSave = (isArchive: boolean) => {
    const validate = () => {
      let errorList: ErrorResponse[] = [];

      spreadsheetState?.results
        .filter((row) => row.updateFlag)
        .forEach((row) => {
          const error: ErrorResponse = {
            index: spreadsheetState?.results.indexOf(row),
            errorColumnList: [],
            messageList: [],
          };

          // Require Items
          if (row.src?.interfaceDefinition.interfaceRequirementType === "") {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "src.interfaceDefinition.interfaceRequirementType",
            });
            error.errorColumnList.push(
              "src.interfaceDefinition.interfaceRequirementType",
            );
          }

          if (
            row.src?.interfaceDefinition.interfaceRequirementType ===
              InterfaceRequirementType.UPDATE &&
            !!!row.src?.interfaceDefinition.metaOnlyFlag
          ) {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "src.interfaceDefinition.metaOnlyFlag",
            });
            error.errorColumnList.push("src.interfaceDefinition.metaOnlyFlag");
          }

          if (
            row.dst?.interfaceDefinition.interfaceRequirementType ===
              InterfaceRequirementType.UPDATE &&
            !!!row.dst?.interfaceDefinition.metaOnlyFlag
          ) {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "dst.interfaceDefinition.metaOnlyFlag",
            });
            error.errorColumnList.push("dst.interfaceDefinition.metaOnlyFlag");
          }

          if (row.src?.interfaceDefinition.dipByRegion === "") {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "src.interfaceDefinition.dipByRegion",
            });
            error.errorColumnList.push("src.interfaceDefinition.dipByRegion");
          }
          if (row.dst?.interfaceDefinition.interfaceRequirementType === "") {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "dst.interfaceDefinition.interfaceRequirementType",
            });
            error.errorColumnList.push(
              "dst.interfaceDefinition.interfaceRequirementType",
            );
          }
          if (row.dst?.interfaceDefinition.dipByRegion === "") {
            error.messageList.push({
              messageId: "APP_SCN_00001",
              message: messageForScreen(ApplicationMessageScreen.APP_SCN_00001),
              errorColumn: "dst.interfaceDefinition.dipByRegion",
            });
            error.errorColumnList.push("dst.interfaceDefinition.dipByRegion");
          }

          if (error.errorColumnList.length > 0) {
            errorList.push(error);
          }
        });
      if (errorList.length === 0) return true;

      const currentSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
        ? [...spreadsheetState.results]
        : [];

      const newSpreadsheetRow = mappingErrorResponseToSpreadsheet(
        errorList,
        currentSpreadsheetRow,
        InterfaceRequirementListPageInitialState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRow],
      });

      return false;
    };

    const getZeroPaddedDateString = (value: string) => {
      const split = value.split(/-|\//);
      return (
        split[0] +
        "/" +
        split[1].padStart(2, "0") +
        "/" +
        split[2].padStart(2, "0")
      );
    };

    const updateIfDefinitions = () => {
      resetUserError();
      const ifList = spreadsheetState?.results
        .filter((row) => row.updateFlag)
        .map((row) => {
          let updateIfList: UpdateIfList = {
            updateDateTime: row.updateDateTime,
            project: {
              ifReqCnclTypeCd: row.project?.ifReqCnclTypeCd ?? "",
              ifReqId: row.project?.ifReqId ?? "",
              pjtId: params.pjtId,
              pjtName: pageState.project?.projectInformation.pjtName ?? "",
            },
            idx: spreadsheetState?.results.indexOf(row),
          };

          // Key Item: DIP By Region
          updateIfList = storeTargetColumn(
            updateIfList,
            "src.interfaceDefinition.dipByRegion",
            row.src?.interfaceDefinition.dipByRegion ?? "",
          );
          updateIfList = storeTargetColumn(
            updateIfList,
            "dst.interfaceDefinition.dipByRegion",
            row.dst?.interfaceDefinition.dipByRegion ?? "",
          );

          // Key Item: InboundId
          if (row.updateColumn.find((e) => e.key === "project.exgInbdId")) {
            updateIfList = storeTargetColumn(
              updateIfList,
              "src.interfaceDefinition.inbound.inboundId",
              row.project?.exgInbdId ?? "",
            );
          }

          // Key Item: Outbound ID
          if (row.updateColumn.find((e) => e.key === "project.exgOtbdId")) {
            updateIfList = storeTargetColumn(
              updateIfList,
              "dst.interfaceDefinition.outbound.outboundId",
              row.project?.exgOtbdId ?? "",
            );
          }

          row.updateColumn.forEach((col) => {
            if (
              [
                "src.interfaceDefinition.outbound.outboundId",
                "src.interfaceDefinition.outbound.outboundIdOptionalPart",
              ].includes(col.key) &&
              col.value === null
            ) {
              // Inter Domain ID, Inter Domain ID Optional Part may be null as a result of the Get IF Definition for Reference API.
              // A null record means that it does not exist as data, so it is excluded from the update.
              return;
            }
            if (
              [
                "project.stgDplymntPlnDate",
                "project.prdDplymntPlnDate",
              ].includes(col.key)
            ) {
              updateIfList = storeTargetColumn(
                updateIfList,
                col.key,
                getZeroPaddedDateString(col.value),
              );
              return;
            }

            updateIfList = storeTargetColumn(updateIfList, col.key, col.value);
          });

          return updateIfList;
        });

      const request = {
        dfnSetVerNum: dfnSetVerNum,
        pjtId: params.pjtId,
        isArchive: isArchive,
        ifCallerScreenType:
          pageState.project?.executionStatus.stsId ===
          TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS
            ? IfCallerScreenType.IF_REQUIREMENT_CHANGE
            : IfCallerScreenType.IF_REQUIREMENT,
        ifList: ifList ?? [],
      };

      updateIfDefinitionsRequest(request);
    };

    if (!validate()) return;

    if (
      !spreadsheetState?.results.some(
        (e) =>
          [IfReqCnclType.CANCEL, IfReqCnclType.PURGE].includes(
            e.project?.ifReqCnclTypeCd as IfReqCnclType,
          ) && e.updateFlag === true,
      )
    ) {
      showConfirmationDialog({
        title: "",
        message: "",
        type: "confirm",
        onOkButtonClicked: updateIfDefinitions,
      });
    } else if (
      [
        TcktStsTypePrjPrgrs.WAITING,
        TcktStsTypePrjPrgrs.ASSIGNING_STAKEHOLDERS,
        TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
      ].includes(
        pageState.project?.executionStatus.stsId as TcktStsTypePrjPrgrs,
      )
    ) {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00127),
        type: "warning",
        onOkButtonClicked: updateIfDefinitions,
      });
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00128),
        type: "warning",
        onOkButtonClicked: updateIfDefinitions,
      });
    }
  };

  const onListenUpdateIfDefinitionsResponse = (): void => {
    if (updateIfDefinitionsResponse === undefined) return;

    // 400 response
    if (
      Array.isArray(updateIfDefinitionsResponse) &&
      updateIfDefinitionsResponse.length > 0
    ) {
      const currentSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
        ? [...spreadsheetState.results]
        : [];

      const newSpreadsheetRow = mappingErrorResponseToSpreadsheet(
        updateIfDefinitionsResponse,
        currentSpreadsheetRow,
        InterfaceRequirementListPageInitialState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRow],
      });
      return;
    }

    // 200 partial success response
    if (
      Array.isArray(updateIfDefinitionsResponse.result) &&
      updateIfDefinitionsResponse.result.length > 0
    ) {
      showAlertBar([
        {
          id: ApplicationMessageScreen.APP_SCN_00165,
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00165),
        },
      ]);

      const currentSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
        ? [...spreadsheetState.results]
        : [];

      const newSpreadsheetRow = mappingErrorResponseToSpreadsheet(
        updateIfDefinitionsResponse.result,
        currentSpreadsheetRow,
        InterfaceRequirementListPageInitialState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRow],
      });
      return;
    }

    showToast({
      design: "completion",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
    });
    onInitialDisplayed();
  };
  useEffect(onListenUpdateIfDefinitionsResponse, [updateIfDefinitionsResponse]);

  const onListenGetIfDefinitionForReferenceResponse = (
    getIfDefinitionForReferenceResponse: GetIfDefinitionForReferenceResponse,
  ): void => {
    if (getIfDefinitionForReferenceResponse === undefined) return;

    if (
      Array.isArray(getIfDefinitionForReferenceResponse.resultList) &&
      getIfDefinitionForReferenceResponse.resultList.some(
        (e) => e.messageList && Object.keys(e.messageList).length > 0,
      )
    ) {
      showToast({
        design: "alert",
        message: messageForScreen(
          ApplicationMessageScreen.APP_SCN_00125,
          "IF definitions that can be modified in the project",
        ),
      });
      const currentSpreadsheetRow: SpreadsheetRow[] = spreadsheetState
        ? [...spreadsheetState.results]
        : [];

      const newSpreadsheetRow = mappingErrorResponseToSpreadsheet(
        getIfDefinitionForReferenceResponse.resultList,
        currentSpreadsheetRow,
        InterfaceRequirementListPageInitialState.displayColumn,
      );

      setSpreadsheetState({
        ...spreadsheetState,
        results: [...newSpreadsheetRow],
      });
    } else if (
      getIfDefinitionForReferenceResponse.resultList.some(
        (e) => Object.keys(e.ifDefinition).length === 0,
      )
    ) {
      showToast({
        design: "alert",
        message: messageForScreen(
          ApplicationMessageScreen.APP_SCN_00125,
          "Specified Boundary ID",
        ),
      });
    }
    const newIfDefinitionReferenceList: GetIfDefinitionForReferenceResult[] =
      JSON.parse(JSON.stringify(pageState.ifDefinitionReferenceList));
    getIfDefinitionForReferenceResponse.resultList.forEach((e) => {
      const index = newIfDefinitionReferenceList.findIndex(
        ({ index, bdryType }) => index == e.index && bdryType === e.bdryType,
      );

      if (index > -1) {
        newIfDefinitionReferenceList[index] = e;
      } else {
        newIfDefinitionReferenceList.push(e);
      }
    });

    setPageState((prevState) => ({
      ...prevState,
      ifDefinitionReferenceList: newIfDefinitionReferenceList,
    }));
  };

  useEffect(() => {
    if (!getIfDefinitionForReferenceResponse) return;
    onListenGetIfDefinitionForReferenceResponse(
      getIfDefinitionForReferenceResponse,
    );
  }, [getIfDefinitionForReferenceResponse]);
  useEffect(() => {
    if (!getIfRefBybulkEditOfInbdIDResponse) return;
    onListenGetIfDefinitionForReferenceResponse(
      getIfRefBybulkEditOfInbdIDResponse,
    );
  }, [getIfRefBybulkEditOfInbdIDResponse]);
  useEffect(() => {
    if (!getIfRefBybulkEditOfOutbdIDResponse) return;
    onListenGetIfDefinitionForReferenceResponse(
      getIfRefBybulkEditOfOutbdIDResponse,
    );
  }, [getIfRefBybulkEditOfOutbdIDResponse]);
  useEffect(() => {
    if (!getIfRefBybulkEditOfDipResponse) return;
    onListenGetIfDefinitionForReferenceResponse(
      getIfRefBybulkEditOfDipResponse,
    );
  }, [getIfRefBybulkEditOfDipResponse]);

  const onUpdateifDefinitionReferenceList = (): void => {
    if (spreadsheetState === undefined) return;
    let newSpreadsheetRow: SpreadsheetRow[] = JSON.parse(
      JSON.stringify(spreadsheetState.results),
    );
    const currentInvalidRecordList: string[] = JSON.parse(
      JSON.stringify(pageState.status.invalidRecordList),
    );
    let newInvalidRecordList: string[] = [];

    const overwriteUpdatedValue = (targetRow: SpreadsheetRow) => {
      const updateColumn = targetRow.updateColumn;
      updateColumn.forEach((e) => {
        targetRow = storeSpreadsheetRecord(targetRow, e.key, e.value);
      });
      return targetRow;
    };

    // Input availability control by presence or absence of Interdomain ID
    // Interdomain ID Optional Part is set to not allowed if it exists, or to allow input if it does not exist.
    const setEditableByInterdomainId = (
      interdomainId: string | undefined,
      spreadsheetRow: SpreadsheetRow,
    ) => {
      if (![undefined, null, ""].includes(interdomainId)) {
        spreadsheetRow.editable[
          "src.interfaceDefinition.outbound.outboundIdOptionalPart"
        ] = EditableType.No;
        return;
      }
      spreadsheetRow.editable[
        "src.interfaceDefinition.outbound.outboundIdOptionalPart"
      ] = EditableType.Yes;
    };

    const setReferenceValue = (
      ref: GetIfDefinitionForReferenceResult,
      newSpreadsheetRow: SpreadsheetRow[],
    ) => {
      if (BdryType.INBOUND === ref.bdryType) {
        return newSpreadsheetRow.map((row, rowIdx) => {
          if (
            !(
              rowIdx === ref.index &&
              row.project?.exgInbdId === ref.bdryId &&
              (row.dst?.interfaceDefinition.dipByRegion === ref.dstDipByRgn ||
                row.project?.exgOtbdId === ref.dstOtbdId)
            )
          ) {
            return row;
          }
          if (row.src === undefined) return row;

          row = {
            ...row,
            src: {
              interfaceDefinition: {
                ...row.src.interfaceDefinition,
                interfaceId: ref.ifDefinition.interfaceDefinition.interfaceId,
                interfaceNameEnglish:
                  ref.ifDefinition.interfaceDefinition.interfaceNameEnglish,
                interfaceNameJapanese:
                  ref.ifDefinition.interfaceDefinition.interfaceNameJapanese,
                dataKindId: ref.ifDefinition.interfaceDefinition.dataKindId,
                dipByRegion: ref.ifDefinition.interfaceDefinition.dipByRegion,
                inbound: ref.ifDefinition.interfaceDefinition.inbound,
                outbound: ref.ifDefinition.interfaceDefinition.outbound,
              },
            },
          };
          if (
            ref.ifDefinition.interfaceDefinition.dataKindId !== undefined &&
            pageState.dataKind.dataKindList.some(
              (e) =>
                e.dtKindId === ref.ifDefinition.interfaceDefinition.dataKindId,
            )
          ) {
            row = storeDataKindInfo(
              row,
              ref.ifDefinition.interfaceDefinition.dataKindId,
            );
          }

          if (ref.ifDefinition.interfaceDefinition.dataKindId !== undefined) {
            row.updateColumn = setUpdateColumn(
              row,
              "src.interfaceDefinition.dataKindId",
              ref.ifDefinition.interfaceDefinition.dataKindId,
            );
            row.updateColumn = setUpdateColumn(
              row,
              "dst.interfaceDefinition.dataKindId",
              ref.ifDefinition.interfaceDefinition.dataKindId,
            );
          }
          if (ref.ifDefinition.interfaceDefinition.interfaceId !== undefined) {
            row.updateColumn = setUpdateColumn(
              row,
              "src.interfaceDefinition.interfaceId",
              ref.ifDefinition.interfaceDefinition.interfaceId,
            );
          }
          if (
            ref.ifDefinition.interfaceDefinition.inbound?.componentId !==
            undefined
          ) {
            row.updateColumn = setUpdateColumn(
              row,
              "src.interfaceDefinition.inbound.componentId",
              ref.ifDefinition.interfaceDefinition.inbound.componentId,
            );
          }
          if (
            ref.ifDefinition.interfaceDefinition.outbound?.outboundId !==
            undefined
          ) {
            row.updateColumn = setUpdateColumn(
              row,
              "src.interfaceDefinition.outbound.outboundId",
              ref.ifDefinition.interfaceDefinition.outbound.outboundId,
            );
          }
          // Input availability control by presence or absence of Interdomain ID
          setEditableByInterdomainId(
            ref.ifDefinition.interfaceDefinition.outbound?.outboundId,
            row,
          );

          if (
            ref.ifDefinition.interfaceDefinition.outbound
              ?.outboundIdOptionalPart !== undefined &&
            ![undefined, null, ""].includes(
              ref.ifDefinition.interfaceDefinition.outbound?.outboundId,
            )
          ) {
            row.updateColumn = setUpdateColumn(
              row,
              "src.interfaceDefinition.outbound.outboundIdOptionalPart",
              ref.ifDefinition.interfaceDefinition.outbound
                .outboundIdOptionalPart,
            );
          }

          // Overwrites the referenced value with the value updated.
          row = overwriteUpdatedValue(row);

          row.errorColumnList = row.errorColumnList.filter(
            (e) => e !== "project.exgInbdId",
          );
          row.messageList = row.messageList.filter(
            (e) => e.errorColumn !== "project.exgInbdId",
          );
          newInvalidRecordList = newInvalidRecordList.filter(
            (e) =>
              e !==
              JSON.stringify({ rowIdx: rowIdx, col: "project.exgInbdId" }),
          );
          return row;
        });
      }
      return newSpreadsheetRow.map((row, rowIdx) => {
        if (
          !(
            rowIdx === ref.index &&
            row.project?.exgOtbdId === ref.bdryId &&
            (!ref.srcInbdId || row.project?.exgInbdId === ref.srcInbdId)
          )
        ) {
          return row;
        }
        if (row.dst === undefined) return row;

        row = {
          ...row,
          dst: {
            interfaceDefinition: {
              ...row.dst.interfaceDefinition,
              interfaceId: ref.ifDefinition.interfaceDefinition.interfaceId,
              dipByRegion: ref.ifDefinition.interfaceDefinition.dipByRegion,
              inbound: ref.ifDefinition.interfaceDefinition.inbound,
              outbound: ref.ifDefinition.interfaceDefinition.outbound,
            },
          },
        };

        /**
         * Set Data Kind-related information on the Destination side.
         *
         * If Src IF Reqirement Type:Create, set the information that could be obtained at the time of reference on the Destination side.
         * Src IF Reqirement Type:If other than Create, set the information that was obtained when the Destination side was referenced only when the Data Kind was not obtained when the Source side was referenced.
         */
        const isSetDestinationDataKind =
          row.src?.interfaceDefinition.interfaceRequirementType ===
            InterfaceRequirementType.CREATE ||
          (row.src?.interfaceDefinition.interfaceRequirementType !==
            InterfaceRequirementType.CREATE &&
            !row.src?.interfaceDefinition.dataKindId);
        if (
          isSetDestinationDataKind &&
          ref.ifDefinition.interfaceDefinition.dataKindId !== undefined &&
          pageState.dataKind.dataKindList.some(
            (e) =>
              e.dtKindId === ref.ifDefinition.interfaceDefinition.dataKindId,
          )
        ) {
          row = storeDataKindInfo(
            row,
            ref.ifDefinition.interfaceDefinition.dataKindId,
          );
        }
        if (ref.ifDefinition.interfaceDefinition.dataKindId !== undefined) {
          row.updateColumn = setUpdateColumn(
            row,
            "dst.interfaceDefinition.dataKindId",
            ref.ifDefinition.interfaceDefinition.dataKindId,
          );
          if (isSetDestinationDataKind) {
            row.updateColumn = setUpdateColumn(
              row,
              "src.interfaceDefinition.dataKindId",
              ref.ifDefinition.interfaceDefinition.dataKindId,
            );
          }
        }

        if (ref.ifDefinition.interfaceDefinition.interfaceId !== undefined) {
          row.updateColumn = setUpdateColumn(
            row,
            "dst.interfaceDefinition.interfaceId",
            ref.ifDefinition.interfaceDefinition.interfaceId,
          );
        }
        if (
          ref.ifDefinition.interfaceDefinition.outbound?.componentId !==
          undefined
        ) {
          row.updateColumn = setUpdateColumn(
            row,
            "dst.interfaceDefinition.outbound.componentId",
            ref.ifDefinition.interfaceDefinition.outbound.componentId,
          );
        }
        if (
          ref.ifDefinition.interfaceDefinition.inbound?.inboundId !== undefined
        ) {
          row.updateColumn = setUpdateColumn(
            row,
            "src.interfaceDefinition.outbound.outboundId",
            ref.ifDefinition.interfaceDefinition.inbound.inboundId,
          );
        }
        // Input availability control by presence or absence of Interdomain ID
        setEditableByInterdomainId(
          ref.ifDefinition.interfaceDefinition.inbound?.inboundId,
          row,
        );

        if (
          ref.ifDefinition.interfaceDefinition.inbound
            ?.inboundIdOptionalPart !== undefined &&
          ![undefined, null, ""].includes(
            ref.ifDefinition.interfaceDefinition.inbound?.inboundId,
          ) &&
          row.src?.interfaceDefinition.interfaceRequirementType !==
            InterfaceRequirementType.CREATE
        ) {
          row.updateColumn = setUpdateColumn(
            row,
            "src.interfaceDefinition.outbound.outboundIdOptionalPart",
            ref.ifDefinition.interfaceDefinition.inbound.inboundIdOptionalPart,
          );
        }

        // Overwrites the referenced value with the value updated.
        row = overwriteUpdatedValue(row);

        row.errorColumnList = row.errorColumnList.filter(
          (e) => e !== "project.exgOtbdId",
        );
        row.messageList = row.messageList.filter(
          (e) => e.errorColumn !== "project.exgOtbdId",
        );
        newInvalidRecordList = newInvalidRecordList.filter(
          (e) =>
            e !== JSON.stringify({ rowIdx: rowIdx, col: "project.exgOtbdId" }),
        );
        return row;
      });
    };

    const setErrorMessage = (
      ref: GetIfDefinitionForReferenceResult,
      newSpreadsheetRow: SpreadsheetRow[],
    ) => {
      if (BdryType.INBOUND === ref.bdryType) {
        return newSpreadsheetRow.map((row, rowIdx) => {
          if (row.project?.exgInbdId !== ref.bdryId) {
            return row;
          }
          if (row.src === undefined) return row;

          const message = {
            messageId: "APP_SCN_00125",
            message: messageForScreen(
              ApplicationMessageScreen.APP_SCN_00125,
              "Specified Inbound ID",
            ),
            errorColumn: "project.exgInbdId",
          };

          const index = row.messageList.findIndex(
            ({ errorColumn, messageId }) =>
              errorColumn === message.errorColumn &&
              messageId === message.messageId,
          );

          if (index < 0) {
            row.messageList.push(message);
            row.errorColumnList.push("project.exgInbdId");
          }
          if (
            !newInvalidRecordList.includes(
              JSON.stringify({ rowIdx: rowIdx, col: "project.exgInbdId" }),
            )
          ) {
            newInvalidRecordList.push(
              JSON.stringify({ rowIdx: rowIdx, col: "project.exgInbdId" }),
            );
          }
          return row;
        });
      }

      return newSpreadsheetRow.map((row, rowIdx) => {
        if (row.project?.exgOtbdId !== ref.bdryId) {
          return row;
        }
        if (row.dst === undefined) return row;

        const message = {
          messageId: "APP_SCN_00125",
          message: messageForScreen(
            ApplicationMessageScreen.APP_SCN_00125,
            "Specified Outbound ID",
          ),
          errorColumn: "project.exgOtbdId",
        };

        const index = row.messageList.findIndex(
          ({ errorColumn, messageId }) =>
            errorColumn === message.errorColumn &&
            messageId === message.messageId,
        );

        if (index < 0) {
          row.messageList.push(message);
          row.errorColumnList.push("project.exgOtbdId");
        }
        if (
          !newInvalidRecordList.includes(
            JSON.stringify({ rowIdx: rowIdx, col: "project.exgOtbdId" }),
          )
        ) {
          newInvalidRecordList.push(
            JSON.stringify({ rowIdx: rowIdx, col: "project.exgOtbdId" }),
          );
        }
        return row;
      });
    };

    // Overwrites the BoudaryID reference information that was obtained.
    pageState.ifDefinitionReferenceList
      .filter((e) => Object.keys(e.ifDefinition).length !== 0)
      .forEach((e) => {
        newSpreadsheetRow = setReferenceValue(e, newSpreadsheetRow);
      });

    // Set error messages for BoudaryIDs for which reference information could not be obtained.
    pageState.ifDefinitionReferenceList
      .filter(
        (e) =>
          e.messageList &&
          Object.keys(e.messageList).length === 0 &&
          Object.keys(e.ifDefinition).length === 0,
      )
      .forEach((e) => {
        newSpreadsheetRow = setErrorMessage(e, newSpreadsheetRow);
      });

    let errorList: ErrorResponse[] = [];
    newSpreadsheetRow.forEach((row, index) => {
      if (row.messageList && row.messageList.length !== 0) {
        errorList.push({
          index: index,
          errorColumnList: row.errorColumnList,
          messageList: row.messageList,
        });
      }
    });

    newSpreadsheetRow = mappingErrorResponseToSpreadsheet(
      errorList,
      newSpreadsheetRow,
      InterfaceRequirementListPageInitialState.displayColumn,
    );

    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRow],
    });

    if (
      JSON.stringify(currentInvalidRecordList) !==
      JSON.stringify(newInvalidRecordList)
    ) {
      setPageState((prevState) => ({
        ...prevState,
        status: {
          ...prevState.status,
          invalidRecordList: newInvalidRecordList,
        },
      }));
    }
  };
  useEffect(onUpdateifDefinitionReferenceList, [
    pageState.ifDefinitionReferenceList,
  ]);

  const Summary: React.FC = () => {
    if (spreadsheetState === undefined) return null;

    const normal = spreadsheetState.results.filter(
      (e) => e.project?.ifReqCnclTypeCd === IfReqCnclType.NORMAL,
    );
    const normalTotal = normal.length.toString();
    const normalDirty = normal.filter((e) => e.updateFlag).length.toString();

    const cancel = spreadsheetState.results.filter(
      (e) => e.project?.ifReqCnclTypeCd === IfReqCnclType.CANCEL,
    );
    const cancelTotal = cancel.length.toString();
    const cancelDirty = cancel.filter((e) => e.updateFlag).length.toString();

    const purge = spreadsheetState.results.filter(
      (e) => e.project?.ifReqCnclTypeCd === IfReqCnclType.PURGE,
    );
    const purgeTotal = purge.length.toString();
    const purgeDirty = purge.filter((e) => e.updateFlag).length.toString();

    const loadedTotal = pageState.status.loadedCount?.toString() ?? "0";

    return (
      <>
        <FrVrfTextDisplayField
          style={{ "--vrf-text-display-field-font-size": "16px" }}
          text={`Total(Unsaved) - Normal: ${normalTotal}(${normalDirty}) Cancel: ${cancelTotal}(${cancelDirty}) Purge: ${purgeTotal}(${purgeDirty}) Loaded: ${loadedTotal}`}
        />
      </>
    );
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginLeft: "30px",
        }}
      >
        <div>
          <Summary />
        </div>
        <div>
          {pageState.authorized && (
            <FrVrfButtonPanel align="right">
              <FrVrfButton
                icon="frIcon:question"
                theme="tertiary"
                size="small"
                tooltipVisible={true}
                tooltipPosition="below"
                tooltipContent={MANUAL_LINK.tooltip}
                onClicked={() => {
                  window.open(MANUAL_LINK.value);
                }}
              />
              <FrVrfFileUploadField
                showConfirm={true}
                uploadBtnText="Load"
                accept=".xlsx"
                visible={[
                  TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
                  TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
                ].includes(
                  pageState.project?.executionStatus
                    .stsId as TcktStsTypePrjPrgrs,
                )}
                onFileSelected={onClickLoad}
                authActId="interface_requirement_list.button.load"
              />
              <FrVrfButton
                content="Download"
                theme="secondary"
                size="small"
                onClicked={onClickDownload}
                disabled={spreadsheetState?.results.some((e) => e.updateFlag)}
                authActId="interface_requirement_list.button.download"
              />
              <FrVrfButton
                content="Add Row"
                theme="secondary"
                size="small"
                visible={[
                  TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
                  TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
                ].includes(
                  pageState.project?.executionStatus
                    .stsId as TcktStsTypePrjPrgrs,
                )}
                onClicked={onClickAddRow}
                authActId="interface_requirement_list.button.add_row"
              />
            </FrVrfButtonPanel>
          )}
        </div>
      </div>

      <FrVrfSpreadsheet
        data={spreadsheetInitialData}
        templateUrl={TEMPLATE_FILE_URL}
        maxHeight={-90}
        onInitialized={initSpreadsheetState}
        onCellEdited={onCellEditedHandle}
        onCellBulkEdited={onCellBulkEditedHandle}
        onCellClicked={onCellClickedHandle}
        onCellSelected={onCellSelectedHandle}
      />

      {pageState.authorized && (
        <FrVrfFooter>
          <FrVrfButton
            slot="left"
            content="Project Plan Page"
            theme="secondary"
            size="large"
            onClicked={onClickProjectPlanPage}
            authActId="interface_requirement_list.button.reload"
          />
          <FrVrfButton
            content="Validate"
            theme="primary"
            size="large"
            onClicked={onClickValidate}
            disabled={spreadsheetState?.results.some((e) => e.updateFlag)}
            authActId="interface_requirement_list.button.save"
          />
          <FrVrfButton
            content="Reload"
            theme="primary"
            size="large"
            onClicked={onClickReload}
            authActId="interface_requirement_list.button.reload"
          />
          <FrVrfButton
            content="Save"
            theme="primary"
            size="large"
            visible={[
              TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
              TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
            ].includes(
              pageState.project?.executionStatus.stsId as TcktStsTypePrjPrgrs,
            )}
            onClicked={() => onClickSave(false)}
            disabled={
              pageState.status.invalidList.length !== 0 ||
              pageState.status.invalidRecordList.length !== 0 ||
              !spreadsheetState?.results.some((e) => e.updateFlag)
            }
            authActId="interface_requirement_list.button.save"
          />
          <FrVrfButton
            content="Save & Backup"
            theme="primary"
            size="large"
            visible={[
              TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
              TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
            ].includes(
              pageState.project?.executionStatus.stsId as TcktStsTypePrjPrgrs,
            )}
            onClicked={() => onClickSave(true)}
            disabled={
              pageState.status.invalidList.length !== 0 ||
              pageState.status.invalidRecordList.length !== 0 ||
              !spreadsheetState?.results.some((e) => e.updateFlag)
            }
            authActId="interface_requirement_list.button.save_and_backup"
          />
        </FrVrfFooter>
      )}
    </>
  );
};
